"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AltiumSchematicTransformer = void 0;
exports.getNoConnectVirtualNetMetadata = getNoConnectVirtualNetMetadata;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const AltiumBusRange_1 = require("../helpers/AltiumBusRange");
const SchDocTypes_1 = require("../SchDocTypes");
const AltiumShapeConverters_1 = require("./AltiumShapeConverters");
const AltiumCombobulatorConfig_1 = require("./AltiumCombobulatorConfig");
const fflate_1 = require("fflate");
const Area_1 = require("../../../../Area");
const project_1 = require("../../project");
const AltiumNameGenerator_1 = require("./AltiumNameGenerator");
const AltiumSpecialStringResolver_1 = require("./AltiumSpecialStringResolver");
const layers_1 = require("../../../layers");
const AltiumEnums_1 = require("../../common/AltiumEnums");
const AltiumLabelTypes_1 = require("../nets/AltiumLabelTypes");
const NetCombobulator_1 = require("../../../../NetCombobulation/NetCombobulator");
const CoordinateConverter_1 = require("../parse/CoordinateConverter");
const fonts_1 = require("../../../../Fonts/fonts");
const PathUtils_1 = require("../../common/PathUtils");
const PropertyParser_1 = require("../parse/PropertyParser");
const SNAPPED_PIN_CONTACT_NOISE_TOLERANCE_MILS = 0.01;
const JUMPER_MODELLED_COMPONENT_KINDS = new Set([
    AltiumEnums_1.ComponentKind.NetTieInBOM,
    AltiumEnums_1.ComponentKind.NetTieNoBOM,
    AltiumEnums_1.ComponentKind.Jumper,
]);
function getNoConnectVirtualNetMetadata({ connections, wires, labels, connectionIsHiddenPin, }) {
    if (connections === 1 && wires === 0 && labels === 0 && connectionIsHiddenPin) {
        return { [sigma_engine_shared_1.VIRTUAL_NET_METADATA_KEY]: sigma_engine_shared_1.NO_CONNECT_VIRTUAL_NET_METADATA_VALUE };
    }
    return undefined;
}
function isJumperModelledComponentKind(component) {
    const kind = component.record.componentkind;
    return kind !== undefined && JUMPER_MODELLED_COMPONENT_KINDS.has(kind);
}
class AltiumSchematicTransformer {
    constructor(schematic, parsedDef, projectConfigUtil, nameGenerator, instances, projectModel, instanceCombobulators, fontMapper, namingStrategy, usedSymbols) {
        this.schematic = schematic;
        this.parsedDef = parsedDef;
        this.projectConfigUtil = projectConfigUtil;
        this.nameGenerator = nameGenerator;
        this.projectModel = projectModel;
        this.fontMapper = fontMapper;
        this.namingStrategy = namingStrategy;
        this.usedSymbols = usedSymbols;
        this.populatedComponentSignalDisplayModes = new WeakMap();
        this.pinNetHiddenPriorities = new Map();
        this.snappedPinContactCandidates = [];
        this.hiddenPinIsolationCounter = 0;
        this.rangedBusDimensionsByAnchor = new Map();
        this.enableDebugOutputs = false;
        this.extractedImages = [];
        this.instances = [];
        this.instanceCombobulators = new Map();
        this.addInstances(instances, instanceCombobulators);
        this.processingResults = {
            type: 'altium-schematic-transformer',
            schematic: this.schematic.fileName,
            message: '',
            innerResults: [],
        };
        this.specialStringResolver = this.createSpecialStringResolver();
    }
    createSpecialStringResolver() {
        const context = {
            ProjectName: () => this.projectConfigUtil.projectFileName ?? this.parsedDef.name ?? this.extractProjectName(),
            SheetNumber: () => this.instances[0]?.hierarchyEntry.sheetNumber?.toString() ??
                this.instances[0]?.hierarchyEntry.schematicIndex?.toString(),
            SheetTotal: () => this.projectConfigUtil.sheetCount.toString(),
            DocumentName: () => this.resolveParameterValue('DocumentName', false) ?? this.extractSheetName(true),
            CurrentDate: () => new Date().toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }),
        };
        return new AltiumSpecialStringResolver_1.AltiumSpecialStringResolver(context, (key) => this.resolveParameterValue(key));
    }
    resolveParameterValue(key, enableProjectLookup = true) {
        const normalizedKey = key.toLowerCase();
        const schematicParams = this.schematic
            .getChildRecords(-1)
            .filter((r) => r.recordType === SchDocTypes_1.RecordType.PARAMETER)
            .filter((p) => p.name.toLowerCase() === normalizedKey);
        if (schematicParams.length > 0) {
            const nonEmptyParams = schematicParams.filter((p) => !['', '*'].includes(p.text.trim()));
            if (nonEmptyParams.length > 1) {
                this.processingResults.innerResults.push({
                    type: 'warning',
                    message: `Multiple non-empty values for parameter ${key}. Using first match.`,
                });
            }
            if (nonEmptyParams.length > 0) {
                return nonEmptyParams[0].text;
            }
        }
        if (enableProjectLookup) {
            return this.projectConfigUtil.getProjectParameter(key);
        }
        return undefined;
    }
    extractProjectName() {
        const schematicFiles = Object.keys(this.parsedDef.schematics);
        if (schematicFiles.length > 0) {
            const fileName = schematicFiles[0].split('/').pop() ?? schematicFiles[0];
            return fileName.replace(/\.[^.]+$/, '');
        }
        return 'Unknown Project';
    }
    get model() {
        if (!this.schematicModel) {
            throw new Error('Schematic model not initialized');
        }
        return this.schematicModel;
    }
    getFinalModel() {
        if (!this.instances) {
            throw new Error('Instances not initialized');
        }
        if (!this.schematicModel) {
            throw new Error('Schematic model not initialized');
        }
        if (this.instanceCombobulators.size !== this.instances.length) {
            throw new Error('Not all instance nets have been generated');
        }
        return this.schematicModel;
    }
    getProjectFontId(altiumFontId) {
        if (altiumFontId === 0 || altiumFontId === '0') {
            return undefined;
        }
        const fontIdNum = typeof altiumFontId === 'string' ? parseInt(altiumFontId, 10) : altiumFontId;
        const localFontMap = this.fontMapper.schematicFontMap.get(this.schematic.fileName);
        if (!localFontMap) {
            return undefined;
        }
        return localFontMap.get(fontIdNum);
    }
    isElectricallyEligible(component) {
        return component.record.componentkind !== AltiumEnums_1.ComponentKind.Graphical;
    }
    addInstances(instances, instanceCombobulators) {
        for (const instance of instances) {
            if (instance.hierarchyEntry.schematic !== this.schematic) {
                throw new Error('All instances must belong to the same schematic');
            }
        }
        const multiInstanceInstance = this.multiInstanceInstance;
        this.instances = [
            ...this.instances.filter((instance) => instance !== multiInstanceInstance),
            ...instances,
            ...(multiInstanceInstance ? [multiInstanceInstance] : []),
        ];
        for (const [instance, combobulator] of instanceCombobulators) {
            this.instanceCombobulators.set(instance, combobulator);
        }
        if (!multiInstanceInstance) {
            this.createMultiInstanceInstance();
        }
    }
    createMultiInstanceInstance() {
        if (!this.instances.some((instance) => instance.isMultiChannel)) {
            return;
        }
        const firstInstance = this.instances[0];
        if (!firstInstance) {
            throw new Error('No first instance found');
        }
        this.multiInstanceInstance = {
            instanceId: `${firstInstance.hierarchyEntry.name}:MULTI-INSTANCE`,
            pathUniqueId: (0, PathUtils_1.joinPathUniqueIds)(firstInstance.pathUniqueId.substring(0, firstInstance.pathUniqueId.lastIndexOf('\\')), `${firstInstance.hierarchyEntry.name}<null>`),
            channelIndex: 0,
            roomName: firstInstance.roomName,
            channelPrefix: firstInstance.channelPrefix,
            numInstances: 1,
            totalSheetInstances: firstInstance.totalSheetInstances,
            designWideInstances: firstInstance.designWideInstances,
            instanceIndex: 0,
            isMultiChannel: false,
            isMultiChannelSchematicInstance: true,
            hierarchyEntry: firstInstance.hierarchyEntry,
            path: Array.from(firstInstance.path).splice(0, firstInstance.path.length - 1),
            children: [],
        };
        this.instanceCombobulators.set(this.multiInstanceInstance, new NetCombobulator_1.NetCombobulator(new sigma_engine_shared_1.NetRefCtx(), this.namingStrategy, this.multiInstanceInstance, {
            pointRoundingPrecision: AltiumCombobulatorConfig_1.ALTIUM_POINT_ROUNDING_PRECISION,
            connectionTolerance: AltiumCombobulatorConfig_1.ALTIUM_CONNECTION_TOLERANCE,
        }));
        this.instances.push(this.multiInstanceInstance);
    }
    prepareModuleAnnotationClaims() {
        for (const instance of this.instances) {
            if (instance.isMultiChannelSchematicInstance) {
                continue;
            }
            for (const component of this.schematic
                .getComponents()
                .filter((candidate) => this.isElectricallyEligible(candidate))) {
                const { sheetDescriptor, nameGenVars } = this.getModuleNameGeneratorArgs(instance, component);
                this.nameGenerator.getAnnotationEntryForPart(sheetDescriptor, { ...nameGenVars, Component: this.getComponentDesignator(component) }, true);
            }
        }
    }
    initializeModuleNames() {
        for (const instance of this.instances) {
            for (const component of this.schematic.getComponents().filter((c) => this.isElectricallyEligible(c))) {
                if (instance.isMultiChannelSchematicInstance) {
                    return null;
                }
                this.computeComponentModuleNames(instance, component, true);
            }
        }
    }
    initializeSchematicModel() {
        if (this.schematicModel) {
            throw new Error('Schematic model already initialized');
        }
        if (this.instances.length === 0) {
            throw new Error('Invariant failed: No instances found. There should always be at least one.');
        }
        this.pinNetHiddenPriorities.clear();
        this.hiddenPinIsolationCounter = 0;
        this.snappedPinContactCandidates.length = 0;
        const sheetIndex = this.instances[0].hierarchyEntry.schematicIndex;
        this.schematicModel = {
            modelType: 'schematic',
            designId: this.schematic.fileName,
            designName: this.extractSheetName(),
            sourceFile: this.schematic.fileName,
            sheetIndex,
            layerSet: layers_1.ALTIUM_SCH_LAYERSET,
            drawings: Object.fromEntries((0, layers_1.getAltiumSchLayers)().map((layer) => [layer.id, []])),
            modules: [],
            nets: {},
            ports: {},
            warnings: [],
            font: {
                default: fonts_1.Font.Newstroke,
            },
            grid: {
                distance: 1.27,
                units: 'mil',
                invertY: false,
            },
            edges_bbox: {
                minx: Number.POSITIVE_INFINITY,
                miny: Number.POSITIVE_INFINITY,
                maxx: Number.NEGATIVE_INFINITY,
                maxy: Number.NEGATIVE_INFINITY,
            },
            initial_zoombox: {
                minx: Number.POSITIVE_INFINITY,
                miny: Number.POSITIVE_INFINITY,
                maxx: Number.NEGATIVE_INFINITY,
                maxy: Number.NEGATIVE_INFINITY,
            },
        };
        if (this.instances[0].numInstances > 1) {
            this.schematicModel.multiInstanceConfiguration = {
                instances: this.instances
                    .filter((instance) => !instance.isMultiChannelSchematicInstance)
                    .map((instance) => ({
                    instanceSheetIndex: instance.hierarchyEntry.schematicIndex,
                    instanceName: `[${sheetIndex + 1}] ${instance.roomName}`,
                    instanceId: instance.pathUniqueId,
                })),
                multiInstanceModules: [],
            };
        }
        this.extractModules(this.schematicModel);
        this.extractPowerPorts(this.nameGenerator, this.schematicModel);
        this.extractAndCombobulateWiresAndJunctions(this.schematicModel);
        this.extractBuses(this.schematicModel);
        this.markBusRangeTaps();
        this.extractSignalHarnesses(this.schematicModel);
        this.extractHarnessConnectors();
        this.extractHarnessConnectorDrawings(this.schematicModel);
        this.extractGraphics(this.schematicModel);
        this.extractImages(this.schematicModel);
        this.extractTextAndLabels(this.schematicModel);
        this.extractSheetSymbols(this.schematicModel);
        this.extractPorts(this.schematicModel);
        this.applySnappedPinContactFallbacks();
        this.calculateBoundingBoxes(this.schematicModel);
        if (this.projectModel.designs[this.schematicModel.designId]) {
            throw new Error(`Design ${this.schematicModel.designId} already exists in project model`);
        }
        this.projectModel.designs[this.schematicModel.designId] = this.schematicModel;
        this.projectModel.processingResults.innerResults.push(this.processingResults);
    }
    extractSheetName(withExtension = false) {
        const filename = this.schematic.fileName.split('/').pop() ?? this.schematic.fileName;
        if (withExtension) {
            return filename;
        }
        return filename.replace(/\.SchDoc$/i, '');
    }
    extractModules(schematicModel) {
        const components = this.schematic.getComponents().filter((c) => this.isElectricallyEligible(c));
        for (const component of components) {
            this.reportUnrecognizedComponentKind(component);
            const modules = this.createDesignModuleFromAltiumComponent(component);
            schematicModel.modules.push(...modules);
        }
    }
    reportUnrecognizedComponentKind(component) {
        const unrecognizedKind = component.record.unrecognizedComponentKind;
        if (unrecognizedKind === undefined) {
            return;
        }
        this.processingResults.innerResults.push({
            type: 'warning',
            message: `Component ${this.getComponentDesignator(component)} declares unknown ComponentKind ${unrecognizedKind}; treating it as a standard part.`,
            record: component.record,
        });
    }
    createDesignModuleFromAltiumComponent(component) {
        const sheet = this.schematic.getSheet();
        const designator = this.getComponentDesignator(component);
        const designatorRecord = component.getDesignator();
        let name = designator;
        if (component.record.partCount > 2) {
            const partNo = component.record.currentPartId;
            name += String.fromCharCode('A'.charCodeAt(0) + partNo - 1);
        }
        const componentRef = this.getComponentNameForAltiumComponent(component);
        const { component: modelComponent, footprintRef } = this.createDesignModelComponentFromAltiumComponent(component);
        const hiddenPinNetNames = new Map(component
            .getPins()
            .map((pin) => [pin.designator, component.getHiddenNetName(pin)])
            .filter((entry) => entry[1] !== undefined));
        const location = [
            CoordinateConverter_1.CoordinateConverter.milsToMm(component.record.location.x),
            CoordinateConverter_1.CoordinateConverter.milsToMm(component.record.location.y),
        ];
        let multiInstanceModule;
        if (this.schematicModel.multiInstanceConfiguration) {
            multiInstanceModule = {
                ref: component.record.uniqueId,
                name: name,
                footprintRef,
                componentRef,
                center: location,
                layer: 'F',
                transform: {
                    pos: [0, 0],
                    angle: (component.record.orientation - 1) * -90,
                    mirrored: [false, component.record.isMirrored],
                },
                drawProperties: {},
                pinNets: {},
                metadata: {
                    'altium.uniqueId': component.record.uniqueId,
                    'altium.partCount': component.record.partCount.toString(),
                    'altium.displayMode': component.record.displayMode.toString(),
                },
            };
            if (designatorRecord) {
                multiInstanceModule.drawProperties['designator'] = {
                    hidden: false,
                    ...PropertyParser_1.PropertyParser.encodeAltiumDisplayText(`${designatorRecord.text}`),
                    angle: designatorRecord.orientation * 90,
                    justify: CoordinateConverter_1.CoordinateConverter.convertJustification(designatorRecord.justification),
                    pos: [
                        CoordinateConverter_1.CoordinateConverter.milsToMm(designatorRecord.location.x),
                        CoordinateConverter_1.CoordinateConverter.milsToMm(designatorRecord.location.y),
                    ],
                    mirror: [false, false],
                    layer: (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.DESIGNATOR).id,
                    ...sheet.getFont(designatorRecord.fontId).textOpts,
                };
            }
            if (this.multiInstanceInstance) {
                this.combobulateModule(this.multiInstanceInstance, modelComponent, multiInstanceModule, [], hiddenPinNetNames);
            }
            this.schematicModel.multiInstanceConfiguration.multiInstanceModules.push(multiInstanceModule);
        }
        return this.instances
            .map((instance) => {
            if (instance.isMultiChannelSchematicInstance) {
                return null;
            }
            const pathUniqueId = (0, PathUtils_1.joinPathUniqueIds)(instance.pathUniqueId, component.record.uniqueId);
            const { instanceName, instanceFullName, nameSuffix } = this.computeComponentModuleNames(instance, component, false);
            const module = {
                ref: `${pathUniqueId}${nameSuffix ?? ''}`,
                name: instanceName,
                fullModuleName: instanceFullName,
                footprintRef,
                componentRef,
                center: location,
                layer: 'F',
                transform: {
                    pos: [0, 0],
                    angle: (component.record.orientation - 1) * -90,
                    mirrored: [false, component.record.isMirrored],
                },
                pinNets: {},
                drawProperties: {},
                metadata: {
                    'altium.uniqueId': component.record.uniqueId,
                    'altium.partCount': component.record.partCount.toString(),
                    'altium.displayMode': component.record.displayMode.toString(),
                },
                multiInstance: multiInstanceModule ?
                    {
                        instanceId: instance.pathUniqueId,
                        displayModuleRef: multiInstanceModule.ref,
                    }
                    : undefined,
            };
            const projectModuleName = instanceFullName ?? instanceName;
            let projectModule = this.projectModel.modules.find((m) => m.name === projectModuleName);
            if (!projectModule) {
                projectModule = {
                    name: projectModuleName,
                    instances: [],
                    componentRef: module.componentRef,
                    metadata: {},
                };
                this.projectModel.modules.push(projectModule);
            }
            if (!projectModule.metadata) {
                projectModule.metadata = {};
            }
            projectModule.instances.push({
                ref: module.ref,
                designId: this.schematicModel.designId,
            });
            const variant = this.projectConfigUtil.getPartVariationForUniqueId(pathUniqueId);
            const variantParamOverrides = variant ? this.projectConfigUtil.getParamVariationsForVariation(variant) : new Map();
            if (variant) {
                projectModule.metadata['altium.variationKind'] = project_1.AltiumVariationKind[variant.Kind];
                if (variant.Kind === project_1.AltiumVariationKind.NotFitted) {
                    projectModule.isDnp = true;
                }
            }
            for (const [column, kind] of this.projectConfigUtil.getUnappliedVariantColumnsForUniqueId(pathUniqueId)) {
                projectModule.metadata[column] = kind;
            }
            if (designatorRecord) {
                module.drawProperties['designator'] = {
                    hidden: false,
                    ...PropertyParser_1.PropertyParser.encodeAltiumDisplayText(instanceName),
                    angle: designatorRecord.orientation * 90,
                    justify: CoordinateConverter_1.CoordinateConverter.convertJustification(designatorRecord.justification),
                    pos: [
                        CoordinateConverter_1.CoordinateConverter.milsToMm(designatorRecord.location.x),
                        CoordinateConverter_1.CoordinateConverter.milsToMm(designatorRecord.location.y),
                    ],
                    mirror: [false, false],
                    layer: (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.DESIGNATOR).id,
                    ...sheet.getFont(designatorRecord.fontId).textOpts,
                };
            }
            const parameters = component.getParameters();
            const componentParamContext = {};
            for (const param of parameters) {
                if (param.name && param.text) {
                    componentParamContext[param.name] = param.text;
                }
            }
            for (const [overrideName, overrideValue] of variantParamOverrides) {
                componentParamContext[overrideName] = overrideValue;
            }
            const componentResolver = this.specialStringResolver.createChildResolver(componentParamContext);
            if (variant?.Kind === project_1.AltiumVariationKind.AlternatePart) {
                projectModule.metadata['altium.alternatePart'] = componentResolver.resolve(variant.AlternatePart);
                const alternateDesignItemId = variant.AltLibLink_DesignItemID?.trim();
                if (alternateDesignItemId) {
                    projectModule.metadata['altium.alternateDesignItemId'] = alternateDesignItemId;
                }
            }
            const appliedParamNames = new Set();
            for (const param of parameters) {
                if (param.name && (param.text || variantParamOverrides.has(param.name))) {
                    appliedParamNames.add(param.name);
                    const resolvedValue = componentResolver.resolve(variantParamOverrides.get(param.name) ?? param.text);
                    module.metadata[param.name] = resolvedValue;
                    projectModule.metadata[param.name] = resolvedValue;
                    const drawProp = {
                        hidden: param.isHidden,
                        ...PropertyParser_1.PropertyParser.encodeAltiumDisplayText(resolvedValue),
                        angle: param.orientation * 90,
                        val: ['comment', 'value'].includes(param.name.toLowerCase()),
                        justify: CoordinateConverter_1.CoordinateConverter.convertJustification(param.justification),
                        pos: [CoordinateConverter_1.CoordinateConverter.milsToMm(param.location.x), CoordinateConverter_1.CoordinateConverter.milsToMm(param.location.y)],
                        mirror: [
                            false,
                            component.record.isMirrored !== param.isMirrored && (component.record.isMirrored || param.isMirrored),
                        ],
                        layer: (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.PARAMETER).id,
                        ...sheet.getFont(param.fontId).textOpts,
                    };
                    module.drawProperties[param.name] = drawProp;
                    if (multiInstanceModule) {
                        multiInstanceModule.drawProperties[param.name] = {
                            ...drawProp,
                            hidden: true,
                        };
                    }
                }
            }
            for (const [overrideName, overrideValue] of variantParamOverrides) {
                if (appliedParamNames.has(overrideName)) {
                    continue;
                }
                const resolvedValue = componentResolver.resolve(overrideValue);
                module.metadata[overrideName] = resolvedValue;
                projectModule.metadata[overrideName] = resolvedValue;
            }
            if (!projectModule.value) {
                projectModule.value = Array.from(Object.values(module.drawProperties)).find((p) => p.val && p.value.trim())?.value;
            }
            const implementationLists = component.getImplementationLists();
            for (const implementationList of implementationLists) {
                for (const implementation of implementationList.getImplementations()) {
                    if (implementation.modelType === 'PCBLIB') {
                        module.metadata['Footprint'] = implementation.modelName;
                        module.metadata['FootprintDescription'] = implementation.description;
                    }
                }
            }
            if (projectModule.name.startsWith('*') &&
                !modelComponent.symbols.find((s) => s.ref === module.footprintRef)?.pins?.length) {
                projectModule.hideInBom = true;
            }
            this.combobulateModule(instance, modelComponent, module, [], hiddenPinNetNames);
            return module;
        })
            .filter((m) => m !== null);
    }
    getComponentDesignator(component) {
        const designatorRecord = component.getDesignator();
        let designator = designatorRecord?.text ?? `Component_${component.record.index}`;
        if (designator === '*') {
            designator = component.record.uniqueId;
        }
        return designator;
    }
    computeComponentModuleNames(instance, component, explicitOnly) {
        const designator = this.getComponentDesignator(component);
        const nameAnnotationMode = AltiumNameGenerator_1.NameGeneratorAnnotationSelectionMode.ExplicitOrChannel;
        const nameSuffix = component.record.partCount > 2 ?
            String.fromCharCode('A'.charCodeAt(0) + component.record.currentPartId - 1)
            : undefined;
        const { sheetDescriptor, nameGenVars } = this.getModuleNameGeneratorArgs(instance, component);
        let instanceName;
        let instanceFullName;
        if (explicitOnly) {
            instanceName = this.nameGenerator.getAnnotationsForModule(designator, sheetDescriptor, nameGenVars, AltiumNameGenerator_1.NameGeneratorDesignatorType.PhysicalInSchematic);
            instanceFullName =
                nameSuffix ?
                    this.nameGenerator.getAnnotationsForModule(designator, sheetDescriptor, nameGenVars, AltiumNameGenerator_1.NameGeneratorDesignatorType.Physical)
                    : undefined;
        }
        else {
            instanceName = this.nameGenerator.generateName(designator, sheetDescriptor, nameGenVars, nameAnnotationMode, AltiumNameGenerator_1.NameGeneratorDesignatorType.PhysicalInSchematic, AltiumNameGenerator_1.NameGeneratorNameType.Module);
            instanceFullName =
                nameSuffix ?
                    this.nameGenerator.generateName(designator, sheetDescriptor, nameGenVars, nameAnnotationMode, AltiumNameGenerator_1.NameGeneratorDesignatorType.Physical, AltiumNameGenerator_1.NameGeneratorNameType.Module)
                    : undefined;
        }
        return { instanceName, instanceFullName, nameSuffix };
    }
    getModuleNameGeneratorArgs(instance, component) {
        const physicalContextParent = instance.inheritsPhysicalNamingContext ? instance.path.at(-1) : undefined;
        const sheetName = instance.inheritsPhysicalNamingContext ? (instance.physicalRoomName ?? instance.roomName) : instance.roomName;
        const roomName = instance.inheritsPhysicalNamingContext ? (instance.physicalRoomName ?? instance.roomName) : instance.roomName;
        const channelIndex = instance.inheritsPhysicalNamingContext ?
            (instance.physicalChannelIndex ?? instance.channelIndex)
            : instance.channelIndex;
        const physicalChannelIndex = instance.physicalChannelIndex ?? instance.channelIndex;
        const channelPrefix = instance.inheritsPhysicalNamingContext ?
            (instance.physicalChannelPrefix ?? instance.channelPrefix)
            : instance.channelPrefix;
        const sheetDescriptor = {
            name: sheetName,
            filename: instance.hierarchyEntry.projectDocumentPath ?? instance.hierarchyEntry.sourceFile,
            uniqueId: instance.sheetSymbol?.uniqueId ?? '',
            pathUniqueId: instance.pathUniqueId,
            roomName,
            channelIndex,
            physicalChannelIndex,
            channelPrefix,
            isMultiChannel: instance.isMultiChannel,
            numInstances: instance.inheritsPhysicalNamingContext ? 1 : instance.numInstances,
            totalSheetInstances: physicalContextParent?.totalSheetInstances ?? instance.totalSheetInstances,
            designWideInstances: physicalContextParent ? physicalContextParent.designWideInstances : instance.designWideInstances,
        };
        const nameGenVars = {
            UniqueID: component.record.uniqueId,
            PathUniqueID: (0, PathUtils_1.joinPathUniqueIds)(instance.pathUniqueId ?? '', component.record.uniqueId),
            PartCount: component.record.partCount - 1,
            PartId: component.record.currentPartId,
            IsPhysicalPart: (component.record.partCount > 1 && component.getImplementationLists().some((i) => i.hasImplementations())) ||
                (component.record.allPinCount ?? 0) > 0,
        };
        return { sheetDescriptor, nameGenVars };
    }
    recordSnappedPinContactCandidate(instance, connection, netPosition, pinLengthMils) {
        const sheet = this.schematic.tryGetSheet();
        if (!sheet?.isSnapGridEnabled()) {
            return;
        }
        const snapGridSizeMils = sheet.getSnapGridSizeMils();
        const snappedPosition = AltiumShapeConverters_1.AltiumShapeConverters.snapPositionToGrid(netPosition, snapGridSizeMils);
        if (!snappedPosition) {
            return;
        }
        const gridResidualMils = Math.abs(pinLengthMils - Math.round(pinLengthMils / snapGridSizeMils) * snapGridSizeMils);
        const maxAxisDisplacementMils = gridResidualMils + SNAPPED_PIN_CONTACT_NOISE_TOLERANCE_MILS;
        const exceedsAllowance = [0, 1].some((axis) => Math.abs(CoordinateConverter_1.CoordinateConverter.mmToMils(snappedPosition[axis] - netPosition[axis])) > maxAxisDisplacementMils);
        if (exceedsAllowance) {
            return;
        }
        this.snappedPinContactCandidates.push({
            instance,
            moduleRef: connection.moduleRef,
            pinRef: connection.pinRef,
            literalPosition: netPosition,
            snappedPosition,
        });
    }
    applySnappedPinContactFallbacks() {
        for (const candidate of this.snappedPinContactCandidates) {
            const combobulator = this.getInstanceCombobulator(candidate.instance);
            const literalContacts = combobulator
                .getObjsAtPosition(candidate.literalPosition, combobulator.defaultDimension.name)
                .filter((object) => {
                const pinConnection = object.connection?.connection;
                return pinConnection?.moduleRef !== candidate.moduleRef || pinConnection.pinRef !== candidate.pinRef;
            });
            if (literalContacts.length > 0) {
                continue;
            }
            const snappedContacts = combobulator.getObjsAtPosition(candidate.snappedPosition, combobulator.defaultDimension.name);
            if (snappedContacts.length === 0) {
                continue;
            }
            combobulator.addWire({ from: candidate.literalPosition, to: candidate.snappedPosition });
        }
    }
    combobulateModule(instance, modelComponent, module, labels = [], hiddenPinNetNames = new Map()) {
        const combobulator = this.getInstanceCombobulator(instance);
        const symbol = modelComponent.symbols.find((s) => s.ref === module.footprintRef);
        if (!symbol) {
            throw new Error(`Symbol ${module.footprintRef} not found for component ${modelComponent.name}`);
        }
        const pos = module.center;
        const angle = module.transform.angle ?? 0;
        const mirrored = module.transform.mirrored ?? [false, false];
        for (const pin of symbol.pins) {
            const netPosition = sigma_engine_shared_1.Utils.Transform.translatePoint(pos, sigma_engine_shared_1.Utils.Transform.getXyWithMirror(sigma_engine_shared_1.Utils.Transform.rotatePoint(pin.start, (mirrored[1] ? 1 : -1) * angle), mirrored));
            if (this.enableDebugOutputs) {
                const layer = (0, layers_1.getAltiumSchLayerByType)(sigma_engine_shared_1.SchLayerType.Other).id;
                if (!this.schematicModel.drawings[layer]) {
                    this.schematicModel.drawings[layer] = [];
                }
                this.schematicModel.drawings[layer].push({
                    type: 'junction',
                    position: netPosition,
                });
                this.schematicModel.drawings[layer].push({
                    type: 'text',
                    ...PropertyParser_1.PropertyParser.encodeAltiumDisplayText(pin.name),
                    pos: sigma_engine_shared_1.Utils.Transform.translatePoint(netPosition, [0.5, 0]),
                    angle: 0,
                    justify: [-1, -1],
                    size: 1,
                    thickness: 1,
                });
            }
            const connection = {
                moduleRef: module.ref,
                pinRef: pin.ref,
                moduleName: module.fullModuleName ?? module.name,
                ...(pin.hidden ? { hiddenPin: true } : {}),
            };
            const connectionPosition = pin.hidden ? [Number.MIN_SAFE_INTEGER, this.hiddenPinIsolationCounter++] : netPosition;
            const netRef = combobulator.addConnection(connectionPosition, connection);
            this.setModulePinNet(module, pin.ref, netRef.strCast(), connection.hiddenPin === true);
            if (!pin.hidden) {
                this.recordSnappedPinContactCandidate(instance, connection, netPosition, Math.hypot(CoordinateConverter_1.CoordinateConverter.mmToMils(pin.end[0] - pin.start[0]), CoordinateConverter_1.CoordinateConverter.mmToMils(pin.end[1] - pin.start[1])));
            }
            const hiddenPinBindingName = pin.hidden ? hiddenPinNetNames.get(pin.ref) : undefined;
            if (hiddenPinBindingName) {
                this.setCombobulatorLabel(instance, connectionPosition, hiddenPinBindingName, 'net-label', {
                    hiddenNetName: hiddenPinBindingName,
                });
            }
            const label = labels.find((l) => l.pinRef === pin.ref);
            if (label) {
                this.setCombobulatorLabel(instance, netPosition, label.value, label.type, label.metadata);
            }
        }
    }
    setModulePinNet(module, pinRef, netName, isHiddenPin) {
        const priorityKey = `${module.ref}\0${pinRef}`;
        const currentAssignmentIsHiddenPin = this.pinNetHiddenPriorities.get(priorityKey);
        if (currentAssignmentIsHiddenPin !== false || !isHiddenPin) {
            module.pinNets[pinRef] = netName;
            this.pinNetHiddenPriorities.set(priorityKey, isHiddenPin);
        }
    }
    isHiddenPinConnection(connection) {
        return connection.hiddenPin === true;
    }
    toPublicNetConnection(connection) {
        const { appliesNetName: _appliesNetName, hiddenPin: _hiddenPin, ...publicConnection } = connection;
        return publicConnection;
    }
    createDesignModelComponentFromAltiumComponent(component) {
        const componentName = this.getComponentNameForAltiumComponent(component);
        const projectComponent = this.projectModel.components[componentName] ??
            this.setComponentModelInformation(component, {
                name: this.getComponentNameForAltiumComponent(component),
                numPins: component.record.allPinCount ?? 0,
                numPads: 0,
                footprints: [],
                symbols: [],
                signals: [],
                isVirtual: component.record.partCount === 1,
                isMultiUnit: component.record.partCount > 1,
                metadata: {
                    Description: component.record.componentDescription,
                    DesignItemId: component.record.designItemId,
                    Library: component.record.sourceLibraryName,
                },
            });
        const allComponentPins = component.getPins();
        const allComponentGraphics = component.getGraphics();
        const packagePartIds = component.record.partCount > 2 ?
            Array.from(new Set([...allComponentPins, ...allComponentGraphics]
                .map((record) => record.ownerPartId ?? -1)
                .filter((ownerPartId) => ownerPartId > 0)))
            : [component.record.currentPartId];
        const sourcePartIds = Array.from(new Set([
            component.record.currentPartId,
            ...packagePartIds.filter((partId) => {
                const partPrefix = `Part${String.fromCharCode('A'.charCodeAt(0) + partId - 1)}::`;
                return !projectComponent.symbols.some((symbol) => symbol.ref.startsWith(partPrefix));
            }),
        ]));
        let footprintRef = this.getSymbolNameForAltiumComponent(component);
        for (const sourcePartId of sourcePartIds) {
            const symbolName = this.getSymbolNameForAltiumComponent(component, sourcePartId);
            const candidate = this.buildPartSymbol(component, sourcePartId, symbolName);
            const existingSymbol = projectComponent.symbols.find((symbol) => symbol.ref === symbolName);
            if (sourcePartId === component.record.currentPartId) {
                if (!existingSymbol) {
                    projectComponent.symbols.push(this.materializePartSymbolDrawings(component, sourcePartId, candidate, false));
                    this.unionPartSignalsWhenModeAlreadyPopulated(projectComponent, component, sourcePartId);
                }
                else if (!this.usedSymbols.has(existingSymbol)) {
                    const existingIndex = projectComponent.symbols.indexOf(existingSymbol);
                    const storedCandidate = this.materializePartSymbolDrawings(component, sourcePartId, candidate, true);
                    projectComponent.symbols.splice(existingIndex, 1, storedCandidate);
                    this.reconcileSignalsForReplacedSymbol(projectComponent, existingSymbol, storedCandidate, component, sourcePartId);
                }
                else if (!this.symbolPinsMatch(existingSymbol, candidate)) {
                    let variant = projectComponent.symbols.find((symbol) => symbol.ref.startsWith(`${symbolName}::Variant`) && this.symbolPinsMatch(symbol, candidate));
                    if (!variant) {
                        const variantName = this.getAvailableVariantSymbolName(projectComponent, symbolName, candidate);
                        variant = this.materializePartSymbolDrawings(component, sourcePartId, {
                            ...candidate,
                            ref: variantName,
                            name: variantName,
                        }, true);
                        projectComponent.symbols.push(variant);
                    }
                    footprintRef = variant.ref;
                    this.unionComponentSignals(projectComponent, this.getPartPins(component, sourcePartId), component.record.displayMode);
                }
            }
            else if (!existingSymbol) {
                projectComponent.symbols.push(this.materializePartSymbolDrawings(component, sourcePartId, candidate, false));
                this.unionPartSignalsWhenModeAlreadyPopulated(projectComponent, component, sourcePartId);
            }
            const populatedDisplayModes = this.populatedComponentSignalDisplayModes.get(projectComponent) ?? new Set();
            if (!populatedDisplayModes.has(component.record.displayMode)) {
                this.unionComponentSignals(projectComponent, allComponentPins.filter((pin) => pin.ownerPartId === -1 || sourcePartIds.includes(pin.ownerPartId)), component.record.displayMode);
                populatedDisplayModes.add(component.record.displayMode);
                this.populatedComponentSignalDisplayModes.set(projectComponent, populatedDisplayModes);
            }
        }
        const footprint = projectComponent.symbols.find((symbol) => symbol.ref === footprintRef);
        if (!footprint) {
            throw new Error(`Missing symbol ${footprintRef} for ${projectComponent.name}`);
        }
        this.usedSymbols.add(footprint);
        this.projectModel.components[projectComponent.name] = projectComponent;
        return { component: projectComponent, footprintRef };
    }
    unionPartSignalsWhenModeAlreadyPopulated(projectComponent, component, partId) {
        if (this.populatedComponentSignalDisplayModes.get(projectComponent)?.has(component.record.displayMode)) {
            this.unionComponentSignals(projectComponent, this.getPartPins(component, partId), component.record.displayMode);
        }
    }
    buildPartSymbol(component, sourcePartId, symbolName) {
        const sourcePins = this.getPartPins(component, sourcePartId);
        const symbolTransforms = this.createSymbolTransforms(component);
        const pinTransforms = [
            symbolTransforms[0],
            {
                pos: [0, 0],
                angle: (component.record.orientation - 5) * -90,
                mirrored: [false, false],
            },
            symbolTransforms[1],
        ];
        const sym = {
            ref: symbolName,
            name: symbolName,
            drawings: [],
            pins: [],
            metadata: {
                LibReference: component.record.libReference,
            },
        };
        for (const pin of sourcePins) {
            const convertedPin = AltiumShapeConverters_1.AltiumShapeConverters.convertPin(pin, component.record.orientation);
            const pinDrawings = convertedPin.drawings;
            const convertedPinGeometry = { ...convertedPin };
            delete convertedPinGeometry.drawings;
            const transformedPin = sigma_engine_shared_1.Utils.Transform.applyTransformsToDrawing(convertedPinGeometry, pinTransforms);
            if (pinDrawings) {
                transformedPin.drawings = pinDrawings.map((drawing, drawingIndex) => {
                    const transformedDrawing = sigma_engine_shared_1.Utils.Transform.applyTransformsToDrawing(drawing.drawing, symbolTransforms);
                    const isPinNameDrawing = pin.showPinName && drawingIndex === (pin.showDesignator ? 1 : 0);
                    if (transformedDrawing.type === 'text' && component.record.isMirrored && !isPinNameDrawing) {
                        transformedDrawing.justify = [-transformedDrawing.justify[0], transformedDrawing.justify[1]];
                    }
                    return {
                        ...drawing,
                        drawing: transformedDrawing,
                    };
                });
            }
            sym.pins.push(transformedPin);
        }
        return sym;
    }
    createSymbolTransforms(component) {
        return [
            {
                pos: [
                    CoordinateConverter_1.CoordinateConverter.milsToMm(-component.record.location.x),
                    CoordinateConverter_1.CoordinateConverter.milsToMm(-component.record.location.y),
                ],
                angle: 0,
                mirrored: [false, false],
            },
            { pos: [0, 0], angle: 0, mirrored: [false, component.record.isMirrored] },
            { pos: [0, 0], angle: (component.record.orientation - 1) * -90, mirrored: [false, false] },
        ];
    }
    getPartPins(component, partId) {
        return component
            .getPins()
            .filter((pin) => (pin.ownerPartId === -1 || pin.ownerPartId === partId) &&
            pin.ownerPartDisplayMode !== -1 &&
            pin.ownerPartDisplayMode === component.record.displayMode);
    }
    materializePartSymbolDrawings(component, sourcePartId, symbol, recoverConversionFailures) {
        const symbolTransforms = this.createSymbolTransforms(component);
        symbol.drawings = component
            .getGraphics()
            .filter((graphic) => ((graphic.ownerPartId ?? 0) === sourcePartId || graphic.ownerPartId === -1) &&
            (graphic.ownerPartDisplayMode ?? 0) === component.record.displayMode)
            .flatMap((graphic) => {
            let converted;
            if (recoverConversionFailures) {
                try {
                    converted = this.convertGraphicRecord(graphic);
                }
                catch (error) {
                    const errorMsg = error instanceof Error ? error.message : 'Unknown error';
                    this.schematicModel.warnings.push(`Failed to convert graphic record ${graphic.recordType} for component ${this.getComponentDesignator(component)} part ${sourcePartId}: ${errorMsg}`);
                    return [];
                }
            }
            else {
                converted = this.convertGraphicRecord(graphic);
            }
            if (!converted) {
                return [];
            }
            const layers = [(0, layers_1.getAltiumSchLayerByType)(sigma_engine_shared_1.SchLayerType.Symbol).id];
            if (converted.type === 'rect' && converted.filled) {
                return [
                    {
                        layers: [(0, layers_1.getAltiumSchLayerByType)(sigma_engine_shared_1.SchLayerType.Background).id],
                        drawing: sigma_engine_shared_1.Utils.Transform.applyTransformsToDrawing(converted, symbolTransforms),
                    },
                    {
                        layers,
                        drawing: sigma_engine_shared_1.Utils.Transform.applyTransformsToDrawing({ ...converted, strokeWidth: Math.max(converted.strokeWidth, 0.254), filled: false }, symbolTransforms),
                    },
                ];
            }
            return [
                {
                    layers,
                    drawing: sigma_engine_shared_1.Utils.Transform.applyTransformsToDrawing(converted.type === 'polygon' ? converted : { ...converted, filled: false }, symbolTransforms),
                },
            ];
        });
        return symbol;
    }
    reconcileSignalsForReplacedSymbol(projectComponent, discardedSymbol, replacementSymbol, component, partId) {
        const stillRepresentedRefs = new Set(projectComponent.symbols
            .filter((symbol) => symbol !== replacementSymbol)
            .flatMap((symbol) => symbol.pins.map((pin) => pin.ref)));
        const removedRefs = new Set(discardedSymbol.pins.map((pin) => pin.ref).filter((ref) => !stillRepresentedRefs.has(ref)));
        for (const signal of projectComponent.signals) {
            signal.pinRefs = signal.pinRefs.filter((ref) => ref === null || !removedRefs.has(ref));
        }
        projectComponent.signals = projectComponent.signals.filter((signal) => signal.pinRefs.length > 0 || signal.padRefs.length > 0);
        this.unionComponentSignals(projectComponent, this.getPartPins(component, partId), component.record.displayMode);
    }
    symbolPinsMatch(left, right) {
        if (left.pins.length !== right.pins.length) {
            return false;
        }
        const leftPins = left.pins.slice().sort((a, b) => this.comparePinIdentityValues(a, b));
        const rightPins = right.pins.slice().sort((a, b) => this.comparePinIdentityValues(a, b));
        return leftPins.every((leftPin, index) => this.pinsMatch(leftPin, rightPins[index]));
    }
    pinsMatch(left, right) {
        return (left.ref === right.ref &&
            left.name === right.name &&
            (left.hidden === true) === (right.hidden === true) &&
            this.pinNumbersMatch(left.start, right.start) &&
            this.pinNumbersMatch(left.end, right.end));
    }
    pinNumbersMatch(left, right) {
        return left.length === right.length && left.every((value, index) => this.pinNumberMatches(value, right[index]));
    }
    pinNumberMatches(left, right) {
        return Math.abs(left - right) <= 0.0001;
    }
    normalizePinCoordinate(value) {
        const normalized = Math.abs(value) < 0.0001 ? 0 : Math.round(value / 0.0001) * 0.0001;
        return Object.is(normalized, -0) ? 0 : normalized;
    }
    getAvailableVariantSymbolName(projectComponent, baseSymbolName, symbol) {
        const pins = symbol.pins
            .map((pin) => ({
            ref: pin.ref,
            name: pin.name,
            hidden: pin.hidden === true,
            start: [this.normalizePinCoordinate(pin.start[0]), this.normalizePinCoordinate(pin.start[1])],
            end: [this.normalizePinCoordinate(pin.end[0]), this.normalizePinCoordinate(pin.end[1])],
        }))
            .sort((a, b) => this.comparePinIdentityValues(a, b));
        const baseName = `${baseSymbolName}::Variant${sigma_engine_shared_1.Utils.fnv1aHash(JSON.stringify(pins))}`;
        let ordinal = 1;
        let variantName = baseName;
        while (projectComponent.symbols.some((existingSymbol) => existingSymbol.ref === variantName)) {
            ordinal += 1;
            variantName = `${baseName}::${ordinal}`;
        }
        return variantName;
    }
    comparePinIdentityValues(left, right) {
        for (const [leftValue, rightValue] of [
            [left.ref, right.ref],
            [left.name, right.name],
            ...left.start.map((value, index) => [value, right.start[index]]),
            ...left.end.map((value, index) => [value, right.end[index]]),
            [left.hidden === true, right.hidden === true],
        ]) {
            if (leftValue < rightValue) {
                return -1;
            }
            if (leftValue > rightValue) {
                return 1;
            }
        }
        return 0;
    }
    unionComponentSignals(projectComponent, allComponentPins, displayMode) {
        const designatorsByName = new Map();
        for (const pin of allComponentPins) {
            const designators = designatorsByName.get(pin.name) ?? new Set();
            designators.add(pin.designator);
            designatorsByName.set(pin.name, designators);
        }
        const duplicateNames = new Set(Array.from(designatorsByName.entries())
            .filter(([, designators]) => designators.size > 1)
            .map(([name]) => name));
        const signalsById = new Map(projectComponent.signals.map((signal) => [signal.id, signal]));
        const signalsByPinRef = new Map(projectComponent.signals.flatMap((signal) => signal.pinRefs.map((pinRef) => [pinRef, signal])));
        for (const pin of allComponentPins) {
            if (pin.ownerPartDisplayMode === -1 || pin.ownerPartDisplayMode !== displayMode) {
                continue;
            }
            if (signalsByPinRef.has(pin.designator)) {
                continue;
            }
            const pinId = duplicateNames.has(pin.name) ? `${pin.designator}_${pin.name}` : pin.name;
            let componentSignal = signalsById.get(pinId);
            if (!componentSignal) {
                componentSignal = {
                    id: pinId,
                    name: pin.name,
                    pinRefs: [],
                    padRefs: [],
                };
                signalsById.set(pinId, componentSignal);
                projectComponent.signals.push(componentSignal);
            }
            if (!componentSignal.pinRefs.includes(pin.designator)) {
                componentSignal.pinRefs.push(pin.designator);
                signalsByPinRef.set(pin.designator, componentSignal);
            }
            switch (pin.electrical) {
                case AltiumEnums_1.ElectricalType.Input:
                    componentSignal.signalDirection = sigma_engine_shared_1.DesignModels.SignalDirection.Input;
                    break;
                case AltiumEnums_1.ElectricalType.Output:
                    componentSignal.signalDirection = sigma_engine_shared_1.DesignModels.SignalDirection.Output;
                    break;
                case AltiumEnums_1.ElectricalType.IO:
                    componentSignal.signalDirection = sigma_engine_shared_1.DesignModels.SignalDirection.BiDirectionalIO;
                    break;
                case AltiumEnums_1.ElectricalType.Power:
                    componentSignal.attributes ??= {};
                    componentSignal.attributes.power = true;
                    break;
                case AltiumEnums_1.ElectricalType.Passive:
                case AltiumEnums_1.ElectricalType.OpenCollector:
                case AltiumEnums_1.ElectricalType.OpenEmitter:
                case AltiumEnums_1.ElectricalType.HiZ:
                    break;
            }
        }
    }
    setComponentModelInformation(component, componentInfo) {
        if (isJumperModelledComponentKind(component)) {
            componentInfo.model = {
                modelId: 'jumper.2h',
            };
        }
        return componentInfo;
    }
    getComponentNameForAltiumComponent(component) {
        const kindSuffix = isJumperModelledComponentKind(component) ? '::jumper' : '';
        return `${this.getLibraryComponentIdentity(component)}${kindSuffix}`;
    }
    getLibraryComponentIdentity(component) {
        if (component.record.designItemId) {
            const symbolRevisionGuid = component.record.symbolRevisionGuid?.trim();
            if (symbolRevisionGuid) {
                return `${component.record.designItemId}::${symbolRevisionGuid}`;
            }
            if (component.record.partCount <= 2) {
                return `${component.record.designItemId}::geometry-${this.getFallbackComponentGeometryHash(component)}`;
            }
            const multipartLibraryIdentity = JSON.stringify({
                sourceLibraryName: component.record.sourceLibraryName,
                libReference: component.record.libReference ?? 'Unknown',
                partCount: component.record.partCount,
            });
            return `${component.record.designItemId}::multipart-${sigma_engine_shared_1.Utils.fnv1aHash(multipartLibraryIdentity)}`;
        }
        else {
            return `${component.record.sourceLibraryName}::${component.record.libReference ?? 'Unknown'}::${component.record.uniqueId}`;
        }
    }
    getFallbackComponentGeometryHash(component) {
        const symbolTransforms = [
            {
                pos: [
                    CoordinateConverter_1.CoordinateConverter.milsToMm(-component.record.location.x),
                    CoordinateConverter_1.CoordinateConverter.milsToMm(-component.record.location.y),
                ],
                angle: 0,
                mirrored: [false, false],
            },
            {
                pos: [0, 0],
                angle: 0,
                mirrored: [false, component.record.isMirrored],
            },
            {
                pos: [0, 0],
                angle: (component.record.orientation - 1) * -90,
                mirrored: [false, false],
            },
        ];
        const pinTransforms = [
            {
                pos: [
                    CoordinateConverter_1.CoordinateConverter.milsToMm(-component.record.location.x),
                    CoordinateConverter_1.CoordinateConverter.milsToMm(-component.record.location.y),
                ],
                angle: 0,
                mirrored: [false, false],
            },
        ];
        const mirrorTransform = {
            pos: [0, 0],
            angle: 0,
            mirrored: [false, component.record.isMirrored],
        };
        const rotationTransform = {
            pos: [0, 0],
            angle: (component.record.orientation - 5) * -90,
            mirrored: [false, false],
        };
        pinTransforms.push(mirrorTransform);
        pinTransforms.push(rotationTransform);
        const pins = component
            .getPins()
            .map((pin) => {
            const convertedPin = AltiumShapeConverters_1.AltiumShapeConverters.convertPin(pin, component.record.orientation);
            const convertedPinGeometry = { ...convertedPin };
            delete convertedPinGeometry.drawings;
            const transformedPin = sigma_engine_shared_1.Utils.Transform.applyTransformsToDrawing(convertedPinGeometry, pinTransforms);
            return this.normalizeGeometrySignature({
                ownerPartId: pin.ownerPartId,
                ownerPartDisplayMode: pin.ownerPartDisplayMode,
                ref: transformedPin.ref,
                name: transformedPin.name,
                number: transformedPin.number,
                start: transformedPin.start,
                end: transformedPin.end,
                electrical: pin.electrical,
                hidden: pin.hidden,
                showPinName: pin.showPinName,
                showDesignator: pin.showDesignator,
            });
        })
            .map((pin) => JSON.stringify(pin))
            .sort();
        const graphics = component
            .getGraphics()
            .flatMap((graphic) => {
            const converted = this.convertGraphicRecord(graphic);
            if (!converted) {
                return [];
            }
            const drawing = converted.type === 'polygon' ? converted : { ...converted, filled: false };
            const transformedDrawing = sigma_engine_shared_1.Utils.Transform.applyTransformsToDrawing(drawing, symbolTransforms);
            return [
                {
                    ownerPartId: graphic.ownerPartId,
                    ownerPartDisplayMode: graphic.ownerPartDisplayMode,
                    drawing: this.canonicalizeFingerprintDrawing(transformedDrawing),
                },
            ];
        })
            .map((graphic) => JSON.stringify(this.normalizeGeometrySignature(graphic)))
            .sort();
        const signature = JSON.stringify({
            sourceLibraryName: component.record.sourceLibraryName,
            libReference: component.record.libReference ?? 'Unknown',
            partCount: component.record.partCount,
            pins,
            graphics,
        });
        return `${sigma_engine_shared_1.Utils.fnv1aHash(signature)}-${signature.length.toString(36)}`;
    }
    canonicalizeFingerprintDrawing(drawing) {
        if (drawing.type !== 'rect') {
            return drawing;
        }
        return {
            ...drawing,
            start: [Math.min(drawing.start[0], drawing.end[0]), Math.min(drawing.start[1], drawing.end[1])],
            end: [Math.max(drawing.start[0], drawing.end[0]), Math.max(drawing.start[1], drawing.end[1])],
        };
    }
    normalizeGeometrySignature(value) {
        if (typeof value === 'number') {
            return Number.isFinite(value) ? Number(value.toFixed(6)) : value;
        }
        if (Array.isArray(value)) {
            return value.map((item) => this.normalizeGeometrySignature(item));
        }
        if (value && typeof value === 'object') {
            return Object.fromEntries(Object.entries(value)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([key, entry]) => [key, this.normalizeGeometrySignature(entry)]));
        }
        return value ?? null;
    }
    getSymbolNameForAltiumComponent(component, partId = component.record.currentPartId) {
        return `Part${String.fromCharCode('A'.charCodeAt(0) + partId - 1)}::Mode${component.record.displayMode ?? 0}::O${component.record.orientation ?? 0}::Mr${component.record.isMirrored ? 1 : 0}`;
    }
    isGroundName(name) {
        const nameLower = name.trim().toLowerCase();
        return (nameLower === 'gnd' ||
            nameLower === 'ground' ||
            nameLower.endsWith('gnd') ||
            nameLower === 'vss' ||
            nameLower === 'vssa' ||
            nameLower === 'vssd');
    }
    extractPowerPorts(nameGenerator, schematicModel) {
        const powerPorts = this.schematic.getPowerPorts();
        if (powerPorts.length === 0) {
            return;
        }
        for (const powerPort of powerPorts) {
            const powerPortName = nameGenerator.generatePowerPortName(powerPort);
            let componentRef = `POWERPORT_${powerPort.style}`;
            if (powerPort.isCrossSheetConnector) {
                componentRef += '_CROSSSHEET';
            }
            const symbolRef = `${powerPort.text}`;
            let component = this.projectModel.components[componentRef];
            if (!component) {
                component = {
                    name: componentRef,
                    numPins: 1,
                    numPads: 0,
                    footprints: [],
                    symbols: [],
                    signals: [
                        {
                            id: '1',
                            name: 'Port',
                            pinRefs: ['1'],
                            padRefs: [],
                        },
                    ],
                    isVirtual: true,
                    metadata: {
                        'altium.powerPort.style': powerPort.style.toString(),
                    },
                };
                this.projectModel.components[componentRef] = component;
            }
            if (!component.symbols.find((s) => s.ref === symbolRef)) {
                try {
                    const { graphics, labelYOffset } = AltiumShapeConverters_1.AltiumShapeConverters.convertPowerPort(powerPort);
                    const symbol = {
                        ref: symbolRef,
                        name: symbolRef,
                        drawings: graphics.map((drawing) => ({
                            layers: [(0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.POWER_PORT).id],
                            drawing,
                        })),
                        pins: [
                            {
                                type: 'pin',
                                ref: '1',
                                name: 'Port',
                                number: '1',
                                start: [0, 0],
                                end: [0, 0],
                                layer: (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.PIN).id,
                                isNetLabel: true,
                            },
                        ],
                        metadata: {
                            'altium.powerPort.text': powerPort.text,
                            'altium.powerPort.style': powerPort.style.toString(),
                            'altium.powerPort.labelY': labelYOffset,
                        },
                    };
                    component.symbols.push(symbol);
                }
                catch (error) {
                    this.processingResults.innerResults.push({
                        type: 'altium-schematic-transformer',
                        severity: sigma_engine_shared_1.DesignModels.BoundaryLevel.Error,
                        message: `Failed to create symbol for power port ${powerPort.text}: ${error instanceof Error ? error.message : String(error)}`,
                        record: powerPort,
                    });
                    continue;
                }
            }
            const symbol = component.symbols.find((s) => s.ref === symbolRef);
            const labelYOffset = symbol?.metadata?.['altium.powerPort.labelY'];
            if (!symbol || typeof labelYOffset !== 'number') {
                throw new Error(`Symbol ${symbolRef} not found for component ${component.name}`);
            }
            let multiInstanceModule;
            if (this.schematicModel.multiInstanceConfiguration) {
                multiInstanceModule = {
                    ref: powerPort.properties.uniqueId?.value ?? powerPort.index.toString(),
                    name: powerPort.text,
                    footprintRef: symbolRef,
                    componentRef,
                    center: [
                        CoordinateConverter_1.CoordinateConverter.milsToMm(powerPort.location.x),
                        CoordinateConverter_1.CoordinateConverter.milsToMm(powerPort.location.y),
                    ],
                    layer: 'F',
                    transform: {
                        pos: [0, 0],
                        angle: powerPort.isCrossSheetConnector ? 0 : ((0, AltiumEnums_1.convertPowerPortOrientation)(powerPort.orientation) - 1) * -90,
                        mirrored: [false, false],
                    },
                    pinNets: {},
                    drawProperties: {
                        value: {
                            pos: [0, 0],
                            layer: (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.POWER_PORT).id,
                            angle: ((0, AltiumEnums_1.convertPowerPortOrientation)(powerPort.orientation) - 1) * -90,
                            justify: [0, 0],
                            size: 1,
                            thickness: 1,
                            mirror: [false, false],
                            hidden: !powerPort.showNetName,
                            ...PropertyParser_1.PropertyParser.encodeAltiumDisplayText(powerPort.text),
                        },
                    },
                    metadata: {
                        'altium.powerPort.text': powerPort.text,
                        'altium.powerPort.style': powerPort.style.toString(),
                        'altium.powerPort.showNetName': powerPort.showNetName.toString(),
                        'altium.index': powerPort.index.toString(),
                    },
                };
                if (this.multiInstanceInstance) {
                    this.combobulateModule(this.multiInstanceInstance, component, multiInstanceModule);
                }
                this.schematicModel.multiInstanceConfiguration.multiInstanceModules.push(multiInstanceModule);
            }
            for (const instance of this.instances) {
                if (instance.isMultiChannelSchematicInstance) {
                    continue;
                }
                const location = [
                    CoordinateConverter_1.CoordinateConverter.milsToMm(powerPort.location.x),
                    CoordinateConverter_1.CoordinateConverter.milsToMm(powerPort.location.y),
                ];
                const valueProperty = {
                    pos: sigma_engine_shared_1.Utils.Transform.translatePoint(location, sigma_engine_shared_1.Utils.Transform.rotatePoint([0, labelYOffset], (powerPort.orientation - 1) * -90)),
                    layer: (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.POWER_PORT).id,
                    angle: 0,
                    justify: [0, 0],
                    size: powerPort.isCrossSheetConnector ? 1.3 : 1.1,
                    thickness: 1,
                    mirror: [false, false],
                    hidden: !powerPort.showNetName,
                    ...PropertyParser_1.PropertyParser.encodeAltiumDisplayText(powerPort.text),
                };
                switch (powerPort.orientation) {
                    case AltiumEnums_1.PortOrientation.RIGHTWARDS:
                        if (powerPort.isCrossSheetConnector) {
                            valueProperty.justify = [-1, 0];
                        }
                        else {
                            valueProperty.justify = [-0.5, 0];
                        }
                        break;
                    case AltiumEnums_1.PortOrientation.UPWARDS:
                        valueProperty.justify = [0, -1];
                        break;
                    case AltiumEnums_1.PortOrientation.LEFTWARDS:
                        if (powerPort.isCrossSheetConnector) {
                            valueProperty.justify = [1, 0];
                        }
                        else {
                            valueProperty.justify = [0.5, 0];
                        }
                        break;
                    case AltiumEnums_1.PortOrientation.DOWNWARDS:
                        valueProperty.justify = [0, 1];
                        break;
                }
                const module = {
                    ref: (0, PathUtils_1.joinPathUniqueIds)(instance.pathUniqueId, powerPort.properties.uniqueid?.value ?? powerPort.index.toString()),
                    name: powerPortName,
                    footprintRef: symbolRef,
                    componentRef,
                    center: location,
                    layer: 'F',
                    transform: {
                        pos: [0, 0],
                        angle: powerPort.isCrossSheetConnector ? 0 : ((0, AltiumEnums_1.convertPowerPortOrientation)(powerPort.orientation) - 1) * -90,
                        mirrored: [false, false],
                    },
                    pinNets: {
                        '1': powerPort.text,
                    },
                    drawProperties: {
                        value: valueProperty,
                    },
                    metadata: {
                        'altium.powerPort.text': powerPort.text,
                        'altium.powerPort.style': powerPort.style.toString(),
                        'altium.powerPort.showNetName': powerPort.showNetName.toString(),
                        'altium.index': powerPort.index.toString(),
                    },
                    multiInstance: multiInstanceModule ?
                        {
                            instanceId: instance.pathUniqueId,
                            displayModuleRef: multiInstanceModule.ref,
                        }
                        : undefined,
                };
                let modelId;
                let voltageValue;
                if (this.isGroundName(powerPort.text)) {
                    modelId = 'ground.standard';
                    voltageValue = undefined;
                }
                else {
                    modelId = 'supply.standard';
                    try {
                        voltageValue = sigma_engine_shared_1.Utils.parseNumericalSIValue(powerPort.text, sigma_engine_shared_1.CommonParameterUnits.voltage);
                    }
                    catch {
                        voltageValue = undefined;
                    }
                }
                let projectModule = this.projectModel.modules.find((m) => m.name === module.name);
                if (!projectModule) {
                    projectModule = {
                        name: module.name,
                        instances: [],
                        componentRef: component.name,
                        hideInBom: true,
                        model: {
                            modelId,
                            inferenceReport: voltageValue !== undefined ?
                                {
                                    parameters: {
                                        voltage: {
                                            source: 'parser',
                                            value: voltageValue,
                                        },
                                    },
                                }
                                : undefined,
                        },
                        value: powerPort.text,
                        metadata: {
                            'altium.powerPort': !powerPort.isCrossSheetConnector,
                            'altium.crossSheetConnector': powerPort.isCrossSheetConnector,
                        },
                    };
                    this.projectModel.modules.push(projectModule);
                }
                else if (voltageValue !== undefined &&
                    (projectModule.model?.modelId !== modelId ||
                        projectModule.model?.inferenceReport?.parameters?.voltage?.value !== voltageValue)) {
                    throw new Error(`Conflicting model information for power port ${powerPort.text} in module ${module.name}`);
                }
                projectModule.instances.push({
                    ref: module.ref,
                    designId: this.schematicModel.designId,
                });
                schematicModel.modules.push(module);
                const labelType = powerPort.isCrossSheetConnector ? 'off-sheet-connector' : 'power-port';
                this.combobulateModule(instance, component, module, [
                    {
                        type: labelType,
                        value: powerPort.text,
                        pinRef: '1',
                        metadata: {},
                    },
                ]);
            }
        }
    }
    extractAndCombobulateWiresAndJunctions(schematicModel) {
        const wireLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.WIRE).id;
        const junctionLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.JUNCTION).id;
        schematicModel.drawings[wireLayerId] = schematicModel.drawings[wireLayerId] ?? [];
        schematicModel.drawings[junctionLayerId] = schematicModel.drawings[junctionLayerId] ?? [];
        const schematicWires = this.schematic.getWires().map((wire) => {
            const converted = AltiumShapeConverters_1.AltiumShapeConverters.convertWire(wire);
            schematicModel.drawings[wireLayerId].push(...converted);
            return converted;
        });
        const junctionPositions = this.schematic.getJunctions().map((junction) => {
            const junctionDrawing = AltiumShapeConverters_1.AltiumShapeConverters.convertJunction(junction);
            schematicModel.drawings[junctionLayerId].push(junctionDrawing);
            return junctionDrawing.position;
        });
        for (const instance of this.instances) {
            const combobulator = this.getInstanceCombobulator(instance);
            for (const wire of schematicWires) {
                for (const track of wire) {
                    const netRef = combobulator.addWire({ from: track.start, to: track.end });
                    this.applyNetRefToGraphic(instance, track, netRef);
                }
            }
            for (const position of junctionPositions) {
                combobulator.addWire({ from: position, to: position });
            }
        }
    }
    extractBuses(schematicModel) {
        const busLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.BUS).id;
        schematicModel.drawings[busLayerId] = schematicModel.drawings[busLayerId] ?? [];
        for (const bus of this.schematic.getBuses()) {
            const converted = AltiumShapeConverters_1.AltiumShapeConverters.convertBus(bus);
            schematicModel.drawings[busLayerId].push(...converted);
        }
        this.prepareRangedBusConductors();
        const schematicBusEntries = this.schematic.getBusEntries().map((busEntry) => {
            const converted = AltiumShapeConverters_1.AltiumShapeConverters.convertBusEntry(busEntry);
            schematicModel.drawings[busLayerId].push(converted);
            return converted;
        });
        for (const instance of this.instances) {
            const combobulator = this.getInstanceCombobulator(instance);
            for (const busEntry of schematicBusEntries) {
                const netRef = combobulator.addWire({
                    from: { position: busEntry.start, dimension: 'bus' },
                    to: { position: busEntry.end, dimension: 'wire' },
                });
                this.applyNetRefToGraphic(instance, busEntry, netRef);
            }
        }
    }
    prepareRangedBusConductors() {
        const hasRangedAnchor = this.schematic.records
            .filter((record) => record.recordType === SchDocTypes_1.RecordType.NET_LABEL)
            .some((record) => AltiumBusRange_1.AltiumBusRange.parse(record.text) !== undefined) ||
            this.schematic.getPorts().some((record) => AltiumBusRange_1.AltiumBusRange.parse(record.name) !== undefined) ||
            this.schematic
                .getSheetSymbols()
                .some((sheetSymbol) => sheetSymbol.getEntries().some((entry) => AltiumBusRange_1.AltiumBusRange.parse(entry.name) !== undefined)) ||
            this.schematic
                .getHarnessConnectors()
                .some((connector) => connector.getEntries().some((entry) => AltiumBusRange_1.AltiumBusRange.parse(entry.name) !== undefined));
        if (!hasRangedAnchor) {
            return;
        }
        const eps = 0.02;
        const segments = [];
        for (const bus of this.schematic.getBuses()) {
            const points = bus.points.map((p) => [
                CoordinateConverter_1.CoordinateConverter.milsToMm(p.x),
                CoordinateConverter_1.CoordinateConverter.milsToMm(p.y),
            ]);
            for (let i = 0; i < points.length - 1; i++) {
                segments.push({ a: points[i], b: points[i + 1], island: segments.length });
            }
        }
        const near = (p, q) => Math.abs(p[0] - q[0]) <= eps && Math.abs(p[1] - q[1]) <= eps;
        const onSegment = (p, s) => {
            const [ax, ay] = s.a;
            const [bx, by] = s.b;
            const length = Math.hypot(bx - ax, by - ay);
            if (length === 0) {
                return near(p, s.a);
            }
            const cross = (bx - ax) * (p[1] - ay) - (by - ay) * (p[0] - ax);
            if (Math.abs(cross) / length > eps) {
                return false;
            }
            const dot = (p[0] - ax) * (bx - ax) + (p[1] - ay) * (by - ay);
            return dot >= -eps * length && dot <= length * length + eps * length;
        };
        const find = (i) => {
            let root = i;
            while (segments[root].island !== root) {
                root = segments[root].island;
            }
            segments[i].island = root;
            return root;
        };
        for (let i = 0; i < segments.length; i++) {
            for (let j = i + 1; j < segments.length; j++) {
                if (onSegment(segments[j].a, segments[i]) ||
                    onSegment(segments[j].b, segments[i]) ||
                    onSegment(segments[i].a, segments[j]) ||
                    onSegment(segments[i].b, segments[j])) {
                    segments[find(j)].island = find(i);
                }
            }
        }
        const anchors = [
            ...this.schematic.records
                .filter((record) => record.recordType === SchDocTypes_1.RecordType.NET_LABEL)
                .flatMap((netLabel) => {
                const range = AltiumBusRange_1.AltiumBusRange.parse(netLabel.text);
                return range ?
                    [
                        {
                            key: `net-label:${netLabel.index}`,
                            position: [
                                CoordinateConverter_1.CoordinateConverter.milsToMm(netLabel.location.x),
                                CoordinateConverter_1.CoordinateConverter.milsToMm(netLabel.location.y),
                            ],
                            width: range.members.length,
                        },
                    ]
                    : [];
            }),
            ...this.schematic.getPorts().flatMap((port) => {
                const range = AltiumBusRange_1.AltiumBusRange.parse(port.name);
                if (!range) {
                    return [];
                }
                const portPosition = [
                    CoordinateConverter_1.CoordinateConverter.milsToMm(port.location.x),
                    CoordinateConverter_1.CoordinateConverter.milsToMm(port.location.y),
                ];
                return this.getPortConnectionPoints(port, portPosition).map((position) => ({
                    key: `port:${port.index}`,
                    position,
                    width: range.members.length,
                }));
            }),
            ...this.schematic.getSheetSymbols().flatMap((sheetSymbol) => sheetSymbol.getEntries().flatMap((entry) => {
                const range = AltiumBusRange_1.AltiumBusRange.parse(entry.name);
                return range ?
                    [
                        {
                            key: `sheet-entry:${entry.index}`,
                            position: AltiumShapeConverters_1.AltiumShapeConverters.calculateSheetEntryPosition(sheetSymbol.record, entry),
                            width: range.members.length,
                        },
                    ]
                    : [];
            })),
            ...this.schematic.getHarnessConnectors().flatMap((connector) => connector.getEntries().flatMap((entry) => {
                const range = AltiumBusRange_1.AltiumBusRange.parse(entry.name);
                return range ?
                    [
                        {
                            key: AltiumSchematicTransformer.harnessEntryAnchorKey(entry),
                            position: this.calculateHarnessEntryPosition(connector.record, entry),
                            width: range.members.length,
                        },
                    ]
                    : [];
            })),
        ];
        const anchorsByIsland = new Map();
        for (const anchor of anchors) {
            const segmentIndex = segments.findIndex((segment) => onSegment(anchor.position, segment));
            if (segmentIndex < 0) {
                continue;
            }
            const island = find(segmentIndex);
            const existing = anchorsByIsland.get(island) ?? [];
            if (!existing.some((candidate) => candidate.key === anchor.key)) {
                anchorsByIsland.set(island, [...existing, anchor]);
            }
        }
        for (const [island, islandAnchors] of anchorsByIsland) {
            if (islandAnchors.length < 2 || !islandAnchors.every((anchor) => anchor.width === islandAnchors[0].width)) {
                continue;
            }
            for (let bitIndex = 0; bitIndex < islandAnchors[0].width; bitIndex++) {
                const dimension = `altium-ranged-bus::${island}::${bitIndex}`;
                for (const instance of this.instances) {
                    const combobulator = this.getInstanceCombobulator(instance);
                    for (let i = 0; i < segments.length; i++) {
                        if (find(i) === island) {
                            combobulator.addWire({
                                from: { position: segments[i].a, dimension },
                                to: { position: segments[i].b, dimension },
                            });
                        }
                    }
                }
                for (const anchor of islandAnchors) {
                    this.rangedBusDimensionsByAnchor.set(anchor.key, [
                        ...(this.rangedBusDimensionsByAnchor.get(anchor.key) ?? []),
                        dimension,
                    ]);
                }
            }
        }
    }
    markBusRangeTaps() {
        const eps = 0.02;
        const segments = [];
        for (const bus of this.schematic.getBuses()) {
            const points = bus.points.map((p) => [
                CoordinateConverter_1.CoordinateConverter.milsToMm(p.x),
                CoordinateConverter_1.CoordinateConverter.milsToMm(p.y),
            ]);
            for (let i = 0; i < points.length - 1; i++) {
                segments.push({ a: points[i], b: points[i + 1], island: segments.length });
            }
        }
        if (segments.length === 0) {
            return;
        }
        const near = (p, q) => Math.abs(p[0] - q[0]) <= eps && Math.abs(p[1] - q[1]) <= eps;
        const onSegment = (p, s) => {
            const [ax, ay] = s.a;
            const [bx, by] = s.b;
            const cross = (bx - ax) * (p[1] - ay) - (by - ay) * (p[0] - ax);
            const length = Math.hypot(bx - ax, by - ay);
            if (length === 0) {
                return near(p, s.a);
            }
            if (Math.abs(cross) / length > eps) {
                return false;
            }
            const dot = (p[0] - ax) * (bx - ax) + (p[1] - ay) * (by - ay);
            return dot >= -eps * length && dot <= length * length + eps * length;
        };
        const findIsland = (i) => {
            let root = i;
            while (segments[root].island !== root) {
                root = segments[root].island;
            }
            segments[i].island = root;
            return root;
        };
        for (let i = 0; i < segments.length; i++) {
            for (let j = i + 1; j < segments.length; j++) {
                if (onSegment(segments[j].a, segments[i]) ||
                    onSegment(segments[j].b, segments[i]) ||
                    onSegment(segments[i].a, segments[j]) ||
                    onSegment(segments[i].b, segments[j])) {
                    segments[findIsland(j)].island = findIsland(i);
                }
            }
        }
        const membersByIsland = new Map();
        const netLabelRecords = this.schematic.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.NET_LABEL);
        for (const netLabel of netLabelRecords) {
            const busRange = AltiumBusRange_1.AltiumBusRange.parse(netLabel.text);
            if (!busRange) {
                continue;
            }
            const anchorPos = [
                CoordinateConverter_1.CoordinateConverter.milsToMm(netLabel.location.x),
                CoordinateConverter_1.CoordinateConverter.milsToMm(netLabel.location.y),
            ];
            for (let i = 0; i < segments.length; i++) {
                if (onSegment(anchorPos, segments[i])) {
                    const island = findIsland(i);
                    const members = membersByIsland.get(island) ?? [];
                    members.push(...busRange.members);
                    membersByIsland.set(island, members);
                    break;
                }
            }
        }
        const addRangeAt = (position, members) => {
            for (let i = 0; i < segments.length; i++) {
                if (onSegment(position, segments[i])) {
                    const island = findIsland(i);
                    membersByIsland.set(island, [...(membersByIsland.get(island) ?? []), ...members]);
                    break;
                }
            }
        };
        for (const port of this.schematic.getPorts()) {
            const busRange = AltiumBusRange_1.AltiumBusRange.parse(port.name);
            if (busRange) {
                const portPosition = [
                    CoordinateConverter_1.CoordinateConverter.milsToMm(port.location.x),
                    CoordinateConverter_1.CoordinateConverter.milsToMm(port.location.y),
                ];
                for (const connectionPoint of this.getPortConnectionPoints(port, portPosition)) {
                    addRangeAt(connectionPoint, busRange.members);
                }
            }
        }
        for (const sheetSymbol of this.schematic.getSheetSymbols()) {
            for (const entry of sheetSymbol.getEntries()) {
                const busRange = AltiumBusRange_1.AltiumBusRange.parse(entry.name);
                if (busRange) {
                    addRangeAt(AltiumShapeConverters_1.AltiumShapeConverters.calculateSheetEntryPosition(sheetSymbol.record, entry), busRange.members);
                }
            }
        }
        if (membersByIsland.size === 0) {
            return;
        }
        for (const [island, members] of membersByIsland) {
            membersByIsland.set(island, Array.from(new Set(members)));
        }
        const markWireTap = (position, members) => {
            for (const instance of this.instances) {
                this.getInstanceCombobulator(instance).addWire({
                    from: position,
                    to: position,
                    strategyMetadata: { busTapMembers: members },
                });
            }
        };
        for (const busEntry of this.schematic.getBusEntries()) {
            const converted = AltiumShapeConverters_1.AltiumShapeConverters.convertBusEntry(busEntry);
            for (let i = 0; i < segments.length; i++) {
                const wireEnd = AltiumSchematicTransformer.getBusEntryWireEndpoint(converted, (position) => onSegment(position, segments[i]));
                if (!wireEnd) {
                    continue;
                }
                const members = membersByIsland.get(findIsland(i));
                if (members) {
                    markWireTap(wireEnd, members);
                }
                break;
            }
        }
        for (const wire of this.schematic.getWires()) {
            for (const track of AltiumShapeConverters_1.AltiumShapeConverters.convertWire(wire)) {
                for (const endpoint of [track.start, track.end]) {
                    for (let i = 0; i < segments.length; i++) {
                        if (!onSegment(endpoint, segments[i])) {
                            continue;
                        }
                        const members = membersByIsland.get(findIsland(i));
                        if (!members) {
                            break;
                        }
                        markWireTap(endpoint, members);
                        break;
                    }
                }
            }
        }
    }
    static getBusEntryWireEndpoint(busEntry, isOnBus) {
        if (isOnBus(busEntry.start)) {
            return busEntry.end;
        }
        if (isOnBus(busEntry.end)) {
            return busEntry.start;
        }
        return undefined;
    }
    extractSignalHarnesses(schematicModel) {
        const signalHarnesses = this.schematic.getSignalHarnesses();
        if (signalHarnesses.length === 0) {
            return;
        }
        const signalHarnessLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.SIGNAL_HARNESS).id;
        schematicModel.drawings[signalHarnessLayerId] = schematicModel.drawings[signalHarnessLayerId] ?? [];
        for (const signalHarness of signalHarnesses) {
            schematicModel.drawings[signalHarnessLayerId].push(...AltiumShapeConverters_1.AltiumShapeConverters.convertSignalHarness(signalHarness));
        }
        for (const instance of this.instances) {
            const combobulator = this.getInstanceCombobulator(instance);
            for (const signalHarness of signalHarnesses) {
                for (const { from, to } of this.convertSignalHarnessToSegments(signalHarness)) {
                    combobulator.addWire({
                        from: { position: from, dimension: AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness },
                        to: { position: to, dimension: AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness },
                        strategyMetadata: {
                            harness: {
                                isCarrier: true,
                                harnessSource: 'signal-harness',
                            },
                        },
                    });
                }
            }
        }
    }
    extractHarnessConnectors() {
        for (const harnessConnector of this.schematic.getHarnessConnectors()) {
            const harnessType = harnessConnector.getHarnessTypeText();
            const entries = harnessConnector.getEntries();
            if (entries.length === 0) {
                continue;
            }
            for (const instance of this.instances) {
                const entryPositions = entries.map((entry) => ({
                    entry,
                    position: this.calculateHarnessEntryPosition(harnessConnector.record, entry),
                }));
                for (const { entry, position } of entryPositions) {
                    const bitDimensions = this.rangedBusDimensionsByAnchor.get(AltiumSchematicTransformer.harnessEntryAnchorKey(entry));
                    const hasOrdinaryConnectivity = !!bitDimensions ||
                        Array.from(this.getInstanceCombobulator(instance).defaultDimension.getObjectsConnectedToPosition(position)).some((object) => !object.label?.strategyMetadata?.harness?.isCarrier);
                    if (this.isHarnessDefinitionEntry(entry) &&
                        !!harnessType?.trim() &&
                        harnessType?.trim() !== '*' &&
                        !hasOrdinaryConnectivity) {
                        this.processingResults.innerResults.push({
                            type: 'info',
                            message: `Harness definition entry ${entry.name} (${harnessType}) has no ordinary connectivity.`,
                            record: entry,
                        });
                    }
                    const bitMembers = bitDimensions ? AltiumBusRange_1.AltiumBusRange.parse(entry.name)?.members : undefined;
                    const members = bitMembers && bitDimensions ?
                        bitMembers.map((name, bitIndex) => ({ name, dimension: bitDimensions[bitIndex] }))
                        : [{ name: entry.name, dimension: undefined }];
                    for (const member of members) {
                        this.setCarrierCombobulatorLabel(instance, position, member.name, 'net-label', {
                            harness: {
                                isCarrier: true,
                                harnessType,
                                harnessMemberName: member.name,
                                harnessMemberType: this.getNestedHarnessEntryType(instance, entry, position),
                                harnessMemberDimension: member.dimension,
                                harnessSource: 'connector-entry',
                                isHarnessDefinitionEntry: this.isHarnessDefinitionEntry(entry),
                            },
                        });
                    }
                }
                const first = entryPositions[0]?.position;
                if (!first) {
                    continue;
                }
                const combobulator = this.getInstanceCombobulator(instance);
                combobulator.addWire({
                    from: {
                        position: this.calculateHarnessConnectorPrimaryPosition(harnessConnector.record),
                        dimension: AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness,
                    },
                    to: { position: first, dimension: AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness },
                    strategyMetadata: {
                        harness: {
                            isCarrier: true,
                            harnessType,
                            harnessSource: 'connector-internal',
                        },
                    },
                });
                for (const { position } of entryPositions.slice(1)) {
                    combobulator.addWire({
                        from: { position: first, dimension: AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness },
                        to: { position, dimension: AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness },
                        strategyMetadata: {
                            harness: {
                                isCarrier: true,
                                harnessType,
                                harnessSource: 'connector-internal',
                            },
                        },
                    });
                }
            }
        }
    }
    extractHarnessConnectorDrawings(schematicModel) {
        const harnessConnectors = this.schematic.getHarnessConnectors();
        if (harnessConnectors.length === 0) {
            return;
        }
        const symbolLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.HARNESS_CONNECTOR).id;
        const labelLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.HARNESS_TYPE).id;
        schematicModel.drawings[symbolLayerId] = schematicModel.drawings[symbolLayerId] ?? [];
        schematicModel.drawings[labelLayerId] = schematicModel.drawings[labelLayerId] ?? [];
        const sheet = this.schematic.getSheet();
        const textOptions = {
            defaultProjectFontId: this.getProjectFontId(1),
            reportedMissingFontIds: new Set(),
        };
        for (const harnessConnector of harnessConnectors) {
            const connectorGroup = AltiumShapeConverters_1.AltiumShapeConverters.convertHarnessConnector(harnessConnector.record);
            if (!connectorGroup) {
                continue;
            }
            for (const entry of harnessConnector.getEntries()) {
                const entryPosition = this.calculateHarnessEntryPosition(harnessConnector.record, entry);
                connectorGroup.drawings.push(AltiumShapeConverters_1.AltiumShapeConverters.convertHarnessEntryMarker(entry, entryPosition));
                const entryText = AltiumShapeConverters_1.AltiumShapeConverters.convertHarnessEntryText(entry, entryPosition, sheet, this.getProjectFontId(entry.textFontId), textOptions);
                if (entryText) {
                    connectorGroup.drawings.push(entryText);
                }
            }
            schematicModel.drawings[symbolLayerId].push(connectorGroup);
            const harnessType = harnessConnector.getHarnessType();
            if (!harnessType) {
                continue;
            }
            const connectorPosition = [
                CoordinateConverter_1.CoordinateConverter.milsToMm(harnessConnector.record.location.x),
                CoordinateConverter_1.CoordinateConverter.milsToMm(harnessConnector.record.location.y),
            ];
            const typeLabel = AltiumShapeConverters_1.AltiumShapeConverters.convertHarnessType(harnessType, connectorPosition, sheet, this.getProjectFontId(harnessType.fontId), textOptions);
            if (typeLabel) {
                schematicModel.drawings[labelLayerId].push(typeLabel);
            }
        }
    }
    calculateHarnessConnectorPrimaryPosition(connector) {
        const connectorX = CoordinateConverter_1.CoordinateConverter.milsToMm(connector.location.x);
        const connectorY = CoordinateConverter_1.CoordinateConverter.milsToMm(connector.location.y);
        const connectorWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(connector.xSize);
        const connectorHeight = CoordinateConverter_1.CoordinateConverter.milsToMm(connector.ySize);
        const primaryConnectionPosition = CoordinateConverter_1.CoordinateConverter.milsToMm(CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(connector.properties, 'primaryconnectionposition'));
        const side = parseInt(connector.properties.harnessconnectorside?.value ?? '0', 10);
        switch (side) {
            case AltiumEnums_1.SheetSymbolSide.Left:
                return [connectorX, connectorY + primaryConnectionPosition];
            case AltiumEnums_1.SheetSymbolSide.Right:
                return [connectorX + connectorWidth, connectorY + primaryConnectionPosition];
            case AltiumEnums_1.SheetSymbolSide.Top:
                return [connectorX + primaryConnectionPosition, connectorY];
            case AltiumEnums_1.SheetSymbolSide.Bottom:
                return [connectorX + primaryConnectionPosition, connectorY + connectorHeight];
            default:
                return [connectorX, connectorY + primaryConnectionPosition];
        }
    }
    convertSignalHarnessToSegments(signalHarness) {
        const points = signalHarness.points.map((point) => [
            CoordinateConverter_1.CoordinateConverter.milsToMm(point.x),
            CoordinateConverter_1.CoordinateConverter.milsToMm(point.y),
        ]);
        const segments = [];
        for (let i = 0; i < points.length - 1; i++) {
            segments.push({ from: points[i], to: points[i + 1] });
        }
        return segments;
    }
    calculateHarnessEntryPosition(connector, entry) {
        const connectorX = CoordinateConverter_1.CoordinateConverter.milsToMm(connector.location.x);
        const connectorY = CoordinateConverter_1.CoordinateConverter.milsToMm(connector.location.y);
        const connectorWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(connector.xSize);
        const connectorHeight = CoordinateConverter_1.CoordinateConverter.milsToMm(connector.ySize);
        const distanceFromTop = CoordinateConverter_1.CoordinateConverter.milsToMm(entry.distanceFromTop) * 10;
        switch (entry.side) {
            case AltiumEnums_1.SheetSymbolSide.Left:
                return [connectorX, connectorY + distanceFromTop];
            case AltiumEnums_1.SheetSymbolSide.Right:
                return [connectorX + connectorWidth, connectorY + distanceFromTop];
            case AltiumEnums_1.SheetSymbolSide.Top:
                return [connectorX + distanceFromTop, connectorY];
            case AltiumEnums_1.SheetSymbolSide.Bottom:
                return [connectorX + distanceFromTop, connectorY + connectorHeight];
            default:
                return [connectorX, connectorY + distanceFromTop];
        }
    }
    getNestedHarnessEntryType(instance, entry, position) {
        if (!entry.harnessType) {
            return undefined;
        }
        const combobulator = this.getInstanceCombobulator(instance);
        if (!combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
            return undefined;
        }
        const touchesSignalHarness = combobulator
            .getDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)
            .getObjectsAtPosition(position)
            .some((object) => object.wire?.strategyMetadata?.harness?.harnessSource === 'signal-harness');
        return touchesSignalHarness ? entry.harnessType : undefined;
    }
    static harnessEntryAnchorKey(entry) {
        return `connector-entry:${entry.index}`;
    }
    isHarnessDefinitionEntry(entry) {
        return entry.properties.ownerindexadditionallist?.value?.toUpperCase() === 'T';
    }
    applyNetRefToGraphic(instance, graphic, netRef) {
        if (instance.isMultiChannelSchematicInstance) {
        }
        else {
            if (!Array.isArray(graphic.nets)) {
                graphic.nets = [];
            }
            graphic.nets.push({
                i: instance.pathUniqueId,
                n: netRef.strCast(),
            });
        }
    }
    finalize(hierarchicalNetGroups) {
        this.finalizeNets(hierarchicalNetGroups);
        (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(this.processingResults);
        const moduleRefs = new Set(this.schematicModel.modules.map((m) => m.ref));
        if (this.schematicModel.modules.length !== moduleRefs.size) {
            const duplicateRefMap = this.schematicModel.modules.reduce((acc, m) => {
                if (acc.has(m.ref)) {
                    acc.get(m.ref).push(m);
                }
                else {
                    acc.set(m.ref, [m]);
                }
                return acc;
            }, new Map());
            const duplicateRefEntries = Array.from(duplicateRefMap.entries()).filter(([, modules]) => modules.length > 1);
            const duplicateRefList = duplicateRefEntries
                .map(([ref, modules]) => `${ref} (${modules.length} times)`)
                .join(', ');
            throw new Error(`Duplicate module refs found in schematic ${this.schematicModel.designId}: ${duplicateRefList}`);
        }
    }
    finalizeNets(hierarchicalNetGroups) {
        if (this.instanceCombobulators.size !== this.instances.length) {
            throw new Error('Not all instance nets have been generated');
        }
        for (const instance of this.instances) {
            if (instance.isMultiChannelSchematicInstance) {
                continue;
            }
            const combobulator = this.getInstanceCombobulator(instance);
            const nets = combobulator.getFinalizedNets();
            for (const net of nets.values()) {
                const netGroup = hierarchicalNetGroups.find((group) => group.contains(net));
                if (!netGroup) {
                    throw new Error(`No net group for net ${net.name.valueOf()} in ${instance.pathUniqueId}`);
                }
                const netName = (netGroup.physicalName ?? net.name).valueOf();
                const connections = netGroup.getConnections(instance);
                for (const [i, nets] of netGroup.getNets()) {
                    if (instance !== i) {
                        continue;
                    }
                    for (const net of nets) {
                        for (const netref of net.netRefs) {
                            netref.setName(netName);
                        }
                    }
                }
                const onlyConnection = net.connections[0]?.connection;
                const metadata = getNoConnectVirtualNetMetadata({
                    connections: net.connections.length,
                    wires: net.wires.length,
                    labels: net.labels.length,
                    connectionIsHiddenPin: onlyConnection !== undefined && this.isHiddenPinConnection(onlyConnection),
                });
                if (!this.schematicModel.nets[netName]) {
                    this.schematicModel.nets[netName] = {
                        name: netName,
                        connections: [],
                        localNames: [],
                        ...(metadata && { metadata }),
                    };
                }
                else if (metadata === undefined) {
                    const aggregate = this.schematicModel.nets[netName];
                    const aggregateMetadata = aggregate.metadata;
                    if (aggregateMetadata?.[sigma_engine_shared_1.VIRTUAL_NET_METADATA_KEY] === sigma_engine_shared_1.NO_CONNECT_VIRTUAL_NET_METADATA_VALUE) {
                        delete aggregateMetadata[sigma_engine_shared_1.VIRTUAL_NET_METADATA_KEY];
                        if (Object.keys(aggregateMetadata).length === 0) {
                            delete aggregate.metadata;
                        }
                    }
                }
                const existingConnections = this.schematicModel.nets[netName].connections;
                for (const connection of connections) {
                    if (!existingConnections.some((c) => c.moduleRef === connection.moduleRef && c.pinRef === connection.pinRef)) {
                        existingConnections.push(this.toPublicNetConnection(connection));
                    }
                }
                for (const connection of connections) {
                    const module = this.schematicModel.modules.find((m) => m.ref === connection.moduleRef);
                    if (!module) {
                        throw new Error(`Missing module ${connection.moduleRef} in design ${this.schematicModel.designId}`);
                    }
                    this.setModulePinNet(module, connection.pinRef, netName, this.isHiddenPinConnection(connection));
                }
            }
        }
    }
    extractGraphics(schematicModel) {
        const graphicRecords = this.schematic.getGraphics();
        const templateIndices = new Set(this.schematic.getTemplates().map((t) => t.index));
        const excludedComponentIndices = new Set(this.schematic
            .getComponents()
            .filter((component) => !this.isElectricallyEligible(component))
            .map((component) => component.record.index));
        const layerId = (0, layers_1.getAltiumSchLayerByType)(layers_1.AltiumSpecificSchLayerTypes.Drawings).id;
        schematicModel.drawings[layerId] = schematicModel.drawings[layerId] ?? [];
        for (const record of graphicRecords) {
            const isOwned = record.ownerIndex !== -1;
            const isTemplateOwned = templateIndices.has(record.ownerIndex);
            if (isOwned && !isTemplateOwned && !excludedComponentIndices.has(record.ownerIndex)) {
                continue;
            }
            try {
                const drawing = this.convertGraphicRecord(record, false);
                if (drawing) {
                    schematicModel.drawings[isTemplateOwned ? (0, layers_1.getAltiumSchLayerByType)(layers_1.AltiumSpecificSchLayerTypes.TitleBlock).id : layerId].push(drawing);
                }
            }
            catch (error) {
                const errorMsg = error instanceof Error ? error.message : 'Unknown error';
                schematicModel.warnings.push(`Failed to convert graphic record ${record.recordType}: ${errorMsg}`);
            }
        }
    }
    convertGraphicRecord(record, invertY = false) {
        switch (record.recordType) {
            case SchDocTypes_1.RecordType.LINE:
                return AltiumShapeConverters_1.AltiumShapeConverters.convertLine(record, invertY);
            case SchDocTypes_1.RecordType.RECTANGLE:
                return AltiumShapeConverters_1.AltiumShapeConverters.convertRectangle(record, invertY);
            case SchDocTypes_1.RecordType.POLYLINE:
                return AltiumShapeConverters_1.AltiumShapeConverters.convertPolyline(record, invertY);
            case SchDocTypes_1.RecordType.POLYGON:
                return AltiumShapeConverters_1.AltiumShapeConverters.convertPolygon(record, invertY);
            case SchDocTypes_1.RecordType.ARC:
                return AltiumShapeConverters_1.AltiumShapeConverters.convertArc(record, invertY);
            case SchDocTypes_1.RecordType.BEZIER:
                return AltiumShapeConverters_1.AltiumShapeConverters.convertBezier(record, invertY);
            case SchDocTypes_1.RecordType.ELLIPSE:
                return AltiumShapeConverters_1.AltiumShapeConverters.convertEllipse(record, invertY);
            case SchDocTypes_1.RecordType.ELLIPTICAL_ARC:
                return AltiumShapeConverters_1.AltiumShapeConverters.convertEllipticalArc(record, invertY);
            case SchDocTypes_1.RecordType.ROUND_RECTANGLE:
                if (record.transparent) {
                    return undefined;
                }
                return AltiumShapeConverters_1.AltiumShapeConverters.convertRoundRectangle(record, invertY);
            default:
                throw new Error(`Unsupported graphic record type: ${record.recordType}`);
        }
    }
    extractImages(schematicModel) {
        const imageRecords = this.schematic.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.IMAGE);
        if (imageRecords.length === 0) {
            return;
        }
        const layerId = (0, layers_1.getAltiumSchLayerByType)(sigma_engine_shared_1.DesignModels.SchLayerType.Image).id;
        schematicModel.drawings[layerId] = schematicModel.drawings[layerId] ?? [];
        const seenRefs = new Set(this.extractedImages.map((e) => e.ref));
        for (const record of imageRecords) {
            const storageName = record.filename;
            if (!storageName) {
                this.processingResults.innerResults.push({
                    type: 'altium-schematic-transformer',
                    severity: sigma_engine_shared_1.DesignModels.BoundaryLevel.Warning,
                    message: 'IMAGE record has no filename',
                });
                continue;
            }
            let imageData = this.schematic.storage.get(storageName);
            if (!imageData) {
                this.processingResults.innerResults.push({
                    type: 'altium-schematic-transformer',
                    severity: sigma_engine_shared_1.DesignModels.BoundaryLevel.Warning,
                    message: `IMAGE record references missing storage entry: ${storageName}`,
                });
                continue;
            }
            if (imageData[0] === 0x78 &&
                (imageData[1] === 0x01 || imageData[1] === 0x5e || imageData[1] === 0x9c || imageData[1] === 0xda)) {
                try {
                    imageData = (0, fflate_1.unzlibSync)(imageData);
                }
                catch (error) {
                    this.processingResults.innerResults.push({
                        type: 'altium-schematic-transformer',
                        severity: sigma_engine_shared_1.DesignModels.BoundaryLevel.Error,
                        message: `Failed to decompress zlib-compressed image data: ${error instanceof Error ? error.message : String(error)}`,
                    });
                    continue;
                }
            }
            const isPng = imageData[0] === 0x89 && imageData[1] === 0x50 && imageData[2] === 0x4e && imageData[3] === 0x47;
            const isJpeg = imageData[0] === 0xff && imageData[1] === 0xd8;
            const isBmp = imageData[0] === 0x42 && imageData[1] === 0x4d;
            const mimeType = isPng ? 'image/png'
                : isJpeg ? 'image/jpeg'
                    : isBmp ? 'image/bmp'
                        : 'application/octet-stream';
            const ext = isPng ? 'png'
                : isJpeg ? 'jpg'
                    : isBmp ? 'bmp'
                        : 'bin';
            const hash = sigma_engine_shared_1.Utils.fnv1aHash(storageName);
            const ref = `images/${hash}.${ext}`;
            const drawing = AltiumShapeConverters_1.AltiumShapeConverters.convertImage(record, ref);
            schematicModel.drawings[layerId].push(drawing);
            if (!seenRefs.has(ref)) {
                seenRefs.add(ref);
                this.extractedImages.push({
                    ref,
                    name: storageName,
                    data: imageData,
                    mimeType,
                });
            }
        }
    }
    extractTextAndLabels(schematicModel) {
        const sheet = this.schematic.getSheet();
        const labelLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.LABEL).id;
        schematicModel.drawings[labelLayerId] = schematicModel.drawings[labelLayerId] ?? [];
        const labelRecords = this.schematic.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.LABEL);
        const componentRecordIndexes = new Set(this.schematic.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.COMPONENT).map((r) => r.index));
        for (const label of labelRecords) {
            if (componentRecordIndexes.has(label.ownerIndex)) {
                const owningComponent = this.schematic.getComponentByIndex(label.ownerIndex);
                if (!owningComponent) {
                    continue;
                }
                if (label.ownerPartId !== undefined &&
                    label.ownerPartId !== -1 &&
                    label.ownerPartId !== owningComponent.record.currentPartId) {
                    continue;
                }
            }
            const projectFontId = this.getProjectFontId(label.fontId);
            const resolvedLabel = this.resolveSpecialStringsInRecord(label, 'text');
            const text = AltiumShapeConverters_1.AltiumShapeConverters.convertLabel(resolvedLabel, sheet, projectFontId);
            schematicModel.drawings[labelLayerId].push(text);
        }
        const textFrameRecords = this.schematic.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.TEXT_FRAME);
        const textFrameLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.TEXT_FRAME).id;
        for (const textFrame of textFrameRecords) {
            const projectFontId = this.getProjectFontId(textFrame.fontId);
            const resolvedTextFrame = this.resolveSpecialStringsInRecord(textFrame, 'text');
            const text = AltiumShapeConverters_1.AltiumShapeConverters.convertTextFrame(resolvedTextFrame, sheet, projectFontId);
            schematicModel.drawings[textFrameLayerId].push(text);
        }
        const netLabelRecords = this.schematic.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.NET_LABEL);
        const netLabelLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.NET_LABEL).id;
        for (const netLabel of netLabelRecords) {
            const projectFontId = this.getProjectFontId(netLabel.fontId);
            const resolvedNetLabel = this.resolveSpecialStringsInRecord(netLabel, 'text');
            const text = AltiumShapeConverters_1.AltiumShapeConverters.convertNetLabel(resolvedNetLabel, sheet, projectFontId);
            schematicModel.drawings[netLabelLayerId].push(text);
            if (netLabel.text.trim() === '') {
                continue;
            }
            const busRange = AltiumBusRange_1.AltiumBusRange.parse(resolvedNetLabel.text);
            if (busRange) {
                for (const instance of this.instances) {
                    const position = [
                        CoordinateConverter_1.CoordinateConverter.milsToMm(netLabel.location.x),
                        CoordinateConverter_1.CoordinateConverter.milsToMm(netLabel.location.y),
                    ];
                    busRange.members.forEach((member, memberIndex) => {
                        const busDimension = this.rangedBusDimensionsByAnchor.get(`net-label:${netLabel.index}`)?.[memberIndex];
                        this.getInstanceCombobulator(instance).setLabel([
                            { position, dimension: `bus-range::${netLabel.index}::${memberIndex}` },
                            ...(busDimension ? [{ position, dimension: busDimension }] : []),
                        ], member, 'net-label', { busRangeMember: true, busRangeNetLabelMember: true });
                    });
                }
                continue;
            }
            for (const instance of this.instances) {
                const position = [
                    CoordinateConverter_1.CoordinateConverter.milsToMm(netLabel.location.x),
                    CoordinateConverter_1.CoordinateConverter.milsToMm(netLabel.location.y),
                ];
                if (this.isPositionOnHarnessCarrier(instance, position) &&
                    !this.isPositionOnRegularWireOrBus(instance, position)) {
                    this.setCarrierCombobulatorLabel(instance, position, resolvedNetLabel.text, 'net-label', {
                        harness: {
                            isCarrier: true,
                            harnessSource: 'carrier-net-label',
                        },
                    });
                }
                else {
                    const netRef = this.setCombobulatorLabel(instance, position, resolvedNetLabel.text, 'net-label', {});
                    this.applyNetRefToGraphic(instance, text, netRef);
                }
            }
        }
    }
    resolveSpecialStringsInRecord(record, textField, resolver) {
        const effectiveResolver = resolver ?? this.specialStringResolver;
        const resolvedText = effectiveResolver.resolve(record[textField]);
        return { ...record, [textField]: resolvedText };
    }
    setCombobulatorLabel(instance, position, label, labelType, strategyMetadata) {
        const combobulator = this.getInstanceCombobulator(instance);
        if (this.enableDebugOutputs) {
            this.schematicModel.drawings[(0, layers_1.getAltiumSchLayerByType)(sigma_engine_shared_1.SchLayerType.Other).id].push({
                type: 'junction',
                position,
            });
        }
        return combobulator.setLabel(position, label, labelType, strategyMetadata);
    }
    setCombobulatorLabelInDimension(instance, position, dimension, label, labelType, strategyMetadata) {
        const combobulator = this.getInstanceCombobulator(instance);
        return combobulator.setLabel([{ position, dimension }], label, labelType, strategyMetadata);
    }
    setRangedHierarchyLabels(instance, positions, dimensionSeed, busRange, labelType, strategyMetadata) {
        const combobulator = this.getInstanceCombobulator(instance);
        let graphicNetRef;
        for (const [index, member] of busRange.members.entries()) {
            const dimension = `altium-range-member::${labelType}::${dimensionSeed}::${index}`;
            const busDimension = this.rangedBusDimensionsByAnchor.get(`${labelType}:${dimensionSeed}`)?.[index];
            const endpoints = [
                ...positions.map((position) => ({ position, dimension })),
                ...(busDimension ? positions.map((position) => ({ position, dimension: busDimension })) : []),
            ];
            const rangeMember = {
                declaration: busRange.declaration,
                index,
                width: busRange.members.length,
            };
            const metadata = {
                ...strategyMetadata,
                [`${labelType}-metadata`]: {
                    ...strategyMetadata[`${labelType}-metadata`],
                    busRange: rangeMember,
                },
            };
            const netRef = combobulator.setLabel(endpoints, member, labelType, metadata);
            if (labelType === 'port' || !busDimension) {
                combobulator.setLabel(endpoints, member, 'net-label', { busRangeMember: true });
            }
            graphicNetRef ??= netRef;
        }
        if (!graphicNetRef) {
            throw new Error(`Cannot expand empty ${labelType} bus range`);
        }
        return graphicNetRef;
    }
    setCombobulatorLabelAtPositions(instance, positions, label, labelType, strategyMetadata) {
        const endpoints = positions.map((position) => ({
            position,
            dimension: AltiumLabelTypes_1.AltiumCombobulationDimensions.Wire,
        }));
        const combobulator = this.getInstanceCombobulator(instance);
        return combobulator.setLabel(endpoints, label, labelType, strategyMetadata);
    }
    setCarrierCombobulatorLabel(instance, position, label, labelType, strategyMetadata) {
        return this.setCarrierCombobulatorLabelAtPositions(instance, [position], label, labelType, strategyMetadata);
    }
    setCarrierCombobulatorLabelAtPositions(instance, positions, label, labelType, strategyMetadata) {
        const endpoints = positions.map((position) => ({
            position,
            dimension: AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness,
        }));
        const combobulator = this.getInstanceCombobulator(instance);
        return combobulator.setLabel(endpoints, label, labelType, strategyMetadata);
    }
    isPositionOnHarnessCarrier(instance, position) {
        const combobulator = this.getInstanceCombobulator(instance);
        if (!combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
            return false;
        }
        const signalHarnessDimension = AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness;
        const harnessDimension = combobulator.getDimension(signalHarnessDimension);
        return Array.from(harnessDimension.getObjectsConnectedToPosition(position)).some((object) => object.wire?.strategyMetadata?.harness?.isCarrier ||
            object.label?.strategyMetadata?.harness?.isCarrier ||
            object.endpoints.some((endpoint) => endpoint.dimension === signalHarnessDimension));
    }
    isPositionOnRegularWireOrBus(instance, position) {
        const combobulator = this.getInstanceCombobulator(instance);
        const hasWireAtPosition = combobulator.defaultDimension
            .getObjectsAtPosition(position)
            .some((object) => !!object.wire);
        const hasBusAtPosition = combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.Bus) &&
            combobulator
                .getDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.Bus)
                .getObjectsAtPosition(position)
                .some((object) => !!object.wire);
        return hasWireAtPosition || hasBusAtPosition;
    }
    getSheetSymbolTargetDesignId(sheetSymbol) {
        for (const instance of this.instances) {
            const child = instance.children.find((candidate) => candidate.sheetSymbol === sheetSymbol);
            if (child) {
                return child.hierarchyEntry.schematic.fileName;
            }
        }
        return null;
    }
    extractSheetSymbols(schematicModel) {
        const sheet = this.schematic.getSheet();
        const sheetSymbols = this.schematic.getSheetSymbols();
        const symbolLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.SHEET_SYMBOL).id;
        const labelLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.LABEL).id;
        const pinLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.SHEET_ENTRY).id;
        schematicModel.drawings[symbolLayerId] = schematicModel.drawings[symbolLayerId] ?? [];
        schematicModel.drawings[labelLayerId] = schematicModel.drawings[labelLayerId] ?? [];
        for (const sheetSymbol of sheetSymbols) {
            try {
                const fileName = sheetSymbol.getFileNameText();
                const sheetName = sheetSymbol.getSheetNameText();
                const sheetUniqueId = sheetSymbol.uniqueId;
                const compName = `schref_${sheetUniqueId}`;
                const sheetWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.record.xSize);
                const sheetHeight = CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.record.ySize);
                if (!this.projectModel.components[compName]) {
                    const entries = sheetSymbol.getEntries();
                    const pins = [];
                    const sheetCenterX = CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.record.location.x + sheetSymbol.record.xSize / 2);
                    const sheetCenterY = CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.record.location.y - sheetSymbol.record.ySize / 2);
                    for (const entry of entries) {
                        const pinName = entry.name;
                        const entryPos = AltiumShapeConverters_1.AltiumShapeConverters.calculateSheetEntryPosition(sheetSymbol.record, entry);
                        const relativePosX = entryPos[0] - sheetCenterX;
                        const relativePosY = entryPos[1] - sheetCenterY - sheetHeight;
                        const relativePos = [relativePosX, relativePosY];
                        const pin = {
                            type: 'pin',
                            ref: pinName,
                            name: pinName,
                            number: pinName,
                            start: relativePos,
                            end: relativePos,
                            layer: pinLayerId,
                            drawings: [],
                        };
                        const iconDrawings = AltiumShapeConverters_1.AltiumShapeConverters.generateSheetEntryIconDrawings(entry, relativePosX, relativePosY);
                        for (const drawing of iconDrawings.drawings) {
                            pin.drawings?.push({
                                layers: [symbolLayerId],
                                drawing,
                            });
                        }
                        const projectFontId = this.getProjectFontId(entry.textFontId);
                        const entryLabel = AltiumShapeConverters_1.AltiumShapeConverters.convertSheetEntryLabel(entry, sheet, relativePos, [iconDrawings.width, iconDrawings.height], projectFontId);
                        pin.drawings.push({
                            layers: [labelLayerId],
                            drawing: entryLabel,
                        });
                        pins.push(pin);
                    }
                    const symbol = {
                        ref: compName,
                        name: compName,
                        pins: pins,
                        drawings: [],
                    };
                    const centeredRect = {
                        type: 'rect',
                        start: [-sheetWidth / 2, -sheetHeight / 2],
                        end: [sheetWidth / 2, sheetHeight / 2],
                        strokeWidth: 0.127,
                        filled: false,
                    };
                    symbol.drawings.push({
                        layers: [symbolLayerId],
                        drawing: centeredRect,
                    });
                    this.projectModel.components[compName] = {
                        name: compName,
                        footprints: [],
                        numPads: 0,
                        numPins: pins.length,
                        symbols: [symbol],
                        signals: pins.map((p) => ({ id: p.ref, name: p.name, pinRefs: [p.ref], padRefs: [] })),
                        isVirtual: true,
                    };
                }
                const sheetCenterX = CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.record.location.x + sheetSymbol.record.xSize / 2);
                const sheetCenterY = CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.record.location.y + sheetSymbol.record.ySize / 2);
                const drawModule = {
                    ref: sheetUniqueId,
                    name: sheetName,
                    fullModuleName: sheetName,
                    footprintRef: compName,
                    componentRef: compName,
                    center: [sheetCenterX, sheetCenterY],
                    layer: 'F',
                    transform: {
                        pos: [0, 0],
                        angle: 0,
                        mirrored: [false, false],
                    },
                    pinNets: {},
                    drawProperties: {},
                    metadata: {
                        'altium.uniqueId': sheetUniqueId,
                        'altium.fileName': fileName,
                        'altium.sheetName': sheetName,
                    },
                };
                const sheetNameRecord = sheetSymbol.getSheetName();
                const fileNameRecord = sheetSymbol.getFileName();
                drawModule.drawProperties['sheetName'] = {
                    ...PropertyParser_1.PropertyParser.encodeAltiumDisplayText(sheetNameRecord.text),
                    pos: [
                        CoordinateConverter_1.CoordinateConverter.milsToMm(sheetNameRecord.location.x),
                        CoordinateConverter_1.CoordinateConverter.milsToMm(sheetNameRecord.location.y),
                    ],
                    layer: labelLayerId,
                    angle: 0,
                    justify: [-1, 1],
                    thickness: 1,
                    mirror: [false, false],
                    hidden: sheetNameRecord.isHidden,
                    ...this.schematic.getSheet().getFont(sheetNameRecord.fontId).textOpts,
                };
                drawModule.drawProperties['fileName'] = {
                    ...PropertyParser_1.PropertyParser.encodeAltiumDisplayText(fileNameRecord.text),
                    pos: [
                        CoordinateConverter_1.CoordinateConverter.milsToMm(fileNameRecord.location.x),
                        CoordinateConverter_1.CoordinateConverter.milsToMm(fileNameRecord.location.y),
                    ],
                    layer: labelLayerId,
                    angle: 0,
                    justify: [-1, 1],
                    thickness: 1,
                    mirror: [false, false],
                    hidden: fileNameRecord.isHidden,
                    ...this.schematic.getSheet().getFont(fileNameRecord.fontId).textOpts,
                };
                if (this.enableDebugOutputs) {
                    const xPos = CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.record.location.x);
                    drawModule.drawProperties['sheetUniqueId'] = {
                        value: `Sheet: ${sheetUniqueId}`,
                        pos: [xPos, sheetCenterY + sheetHeight / 2 + 1],
                        layer: (0, layers_1.getAltiumSchLayerByType)(sigma_engine_shared_1.DesignModels.SchLayerType.Other).id,
                        angle: 0,
                        justify: [-1, -1],
                        size: 1.2,
                        thickness: 1,
                        mirror: [false, false],
                    };
                    const path = this.instances[0].pathUniqueId;
                    drawModule.drawProperties['pathUniqueId'] = {
                        value: `Path: ${path || '<root>'}`,
                        pos: [xPos, sheetCenterY + sheetHeight / 2 + 3.2],
                        layer: (0, layers_1.getAltiumSchLayerByType)(sigma_engine_shared_1.DesignModels.SchLayerType.Other).id,
                        angle: 0,
                        justify: [-1, -1],
                        size: 1.2,
                        thickness: 1,
                        mirror: [false, false],
                    };
                }
                schematicModel.modules.push(drawModule);
                const schRef = {
                    type: 'schref',
                    file: fileName,
                    name: sheetName,
                    designId: this.getSheetSymbolTargetDesignId(sheetSymbol),
                    drawModule: drawModule,
                };
                schematicModel.drawings[symbolLayerId].push(schRef);
                const entries = sheetSymbol.getEntries();
                for (const entry of entries) {
                    for (const instance of this.instances) {
                        const position = AltiumShapeConverters_1.AltiumShapeConverters.calculateSheetEntryPosition(sheetSymbol.record, entry);
                        const repeatMatch = /^\s*REPEAT\((.*)\)\s*$/i.exec(entry.name)?.[1]?.trim();
                        const childChannelIndexes = [];
                        for (const childInstance of instance.children) {
                            if (childInstance.sheetSymbol !== sheetSymbol || !childInstance.path.includes(instance)) {
                                continue;
                            }
                            if (childChannelIndexes.indexOf(childInstance.channelIndex) !== -1) {
                                throw new Error(`Channel index ${childInstance.channelIndex} is already in use for sheet entry ${entry.name}`);
                            }
                            childChannelIndexes.push(childInstance.channelIndex);
                        }
                        const sheetEntryMetadata = {
                            sheetSymbolUniqueId: sheetUniqueId,
                            parentPathUniqueId: instance.pathUniqueId,
                            sheetSymbolName: sheetName,
                            sheetSymbolFileName: fileName,
                            sheetEntryName: repeatMatch ?? entry.name,
                            sheetEntryText: entry.name,
                            isRepeated: !!repeatMatch,
                            channelIndexes: childChannelIndexes.sort(),
                            ioType: entry.ioType,
                        };
                        const touchesSignalHarness = () => {
                            const combobulator = this.getInstanceCombobulator(instance);
                            if (!combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
                                return false;
                            }
                            const harnessDimension = combobulator.getDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
                            return Array.from(harnessDimension.getObjectsAtPosition(position)).length > 0;
                        };
                        if (entry.harnessType || touchesSignalHarness()) {
                            this.setCarrierCombobulatorLabel(instance, position, entry.name, 'sheet-entry', {
                                'sheet-entry-metadata': sheetEntryMetadata,
                                harness: {
                                    isCarrier: true,
                                    harnessType: entry.harnessType || undefined,
                                    harnessSource: 'sheet-entry',
                                },
                            });
                        }
                        else {
                            const busRange = AltiumBusRange_1.AltiumBusRange.parse(entry.name);
                            if (busRange) {
                                this.setRangedHierarchyLabels(instance, [position], String(entry.index), busRange, 'sheet-entry', {
                                    'sheet-entry-metadata': sheetEntryMetadata,
                                });
                            }
                            else {
                                this.setCombobulatorLabel(instance, position, entry.name, 'sheet-entry', {
                                    'sheet-entry-metadata': sheetEntryMetadata,
                                });
                            }
                        }
                    }
                }
            }
            catch (error) {
                this.processingResults.innerResults.push({
                    type: 'altium-schematic-transformer',
                    severity: sigma_engine_shared_1.DesignModels.BoundaryLevel.Error,
                    message: `Failed to process sheet symbol ${sheetSymbol.record.index}: ${error instanceof Error ? error.message : String(error)}`,
                    record: sheetSymbol.record,
                });
            }
        }
    }
    extractPorts(schematicModel) {
        const ports = this.schematic.getPorts();
        if (ports.length === 0) {
            return;
        }
        const sheet = this.schematic.getSheet();
        const labelLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.LABEL).id;
        schematicModel.drawings[labelLayerId] = schematicModel.drawings[labelLayerId] ?? [];
        for (const port of ports) {
            try {
                const projectFontId = this.getProjectFontId(port.fontId);
                const portGroup = AltiumShapeConverters_1.AltiumShapeConverters.convertPort(port, sheet, projectFontId);
                schematicModel.drawings[labelLayerId].push(portGroup);
                const connectionPoints = this.getPortConnectionPoints(port, portGroup.pos);
                for (const instance of this.instances) {
                    const portMetadata = {
                        portName: port.name,
                        ioType: port.ioType,
                        pathUniqueId: instance.pathUniqueId,
                        channelIndex: instance.channelIndex,
                    };
                    const touchesSignalHarness = () => {
                        const combobulator = this.getInstanceCombobulator(instance);
                        if (!combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
                            return false;
                        }
                        const harnessDimension = combobulator.getDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
                        return connectionPoints.some((point) => Array.from(harnessDimension.getObjectsAtPosition(point)).length > 0);
                    };
                    if (port.harnessType || touchesSignalHarness()) {
                        this.setCarrierCombobulatorLabelAtPositions(instance, this.getHarnessPortCarrierPositions(portGroup.pos, connectionPoints), port.name, 'port', {
                            'port-metadata': portMetadata,
                            harness: {
                                isCarrier: true,
                                harnessType: port.harnessType || undefined,
                                harnessSource: 'port',
                            },
                        });
                    }
                    else {
                        const busRange = AltiumBusRange_1.AltiumBusRange.parse(port.name);
                        const graphicNetRef = busRange ?
                            this.setRangedHierarchyLabels(instance, connectionPoints, String(port.index), busRange, 'port', {
                                'port-metadata': portMetadata,
                            })
                            : this.setCombobulatorLabelAtPositions(instance, connectionPoints, port.name, 'port', {
                                'port-metadata': portMetadata,
                            });
                        this.applyNetRefToGraphic(instance, portGroup, graphicNetRef);
                    }
                }
            }
            catch (error) {
                this.processingResults.innerResults.push({
                    type: 'altium-schematic-transformer',
                    severity: sigma_engine_shared_1.DesignModels.BoundaryLevel.Error,
                    message: `Failed to process port ${port.index}: ${error instanceof Error ? error.message : String(error)}`,
                    record: port,
                });
            }
        }
    }
    getPortConnectionPoints(port, portPosition) {
        const portWidthMm = CoordinateConverter_1.CoordinateConverter.milsToMm(port.width);
        const isVertical = port.style === AltiumEnums_1.PortStyle.NoneVertical ||
            port.style === AltiumEnums_1.PortStyle.Top ||
            port.style === AltiumEnums_1.PortStyle.Bottom ||
            port.style === AltiumEnums_1.PortStyle.TopBottom;
        const farEnd = isVertical ? [portPosition[0], portPosition[1] - portWidthMm] : [portPosition[0] + portWidthMm, portPosition[1]];
        return this.dedupePositions([portPosition, farEnd]);
    }
    getHarnessPortCarrierPositions(portPosition, connectionPoints) {
        return this.dedupePositions([portPosition, ...connectionPoints]);
    }
    dedupePositions(positions) {
        const seen = new Set();
        return positions.filter((position) => {
            const key = `${position[0]}:${position[1]}`;
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    }
    getInstanceCombobulator(instance) {
        const combobulator = this.instanceCombobulators.get(instance);
        if (!combobulator) {
            throw new Error(`No combobulator found for instance ${instance?.pathUniqueId ?? 'null'}`);
        }
        return combobulator;
    }
    calculateBoundingBoxes(schematicModel) {
        const layerSets = (0, layers_1.getAltiumDefaultLayerSets)();
        const bbox = new Area_1.default();
        bbox.extendFromSchModel(this.projectModel, schematicModel, layerSets[layers_1.ALTIUM_SCH_LAYERSET].layers, this.projectModel.warnings);
        schematicModel.edges_bbox = {
            minx: bbox.xMin,
            miny: bbox.yMin,
            maxx: bbox.xMax,
            maxy: bbox.yMax,
        };
        const visibleBbox = new Area_1.default();
        visibleBbox.extendFromSchModel(this.projectModel, schematicModel, layerSets[layers_1.ALTIUM_SCH_LAYERSET].layers, this.projectModel.warnings, false);
        schematicModel.initial_zoombox = {
            minx: visibleBbox.xMin,
            miny: visibleBbox.yMin,
            maxx: visibleBbox.xMax,
            maxy: visibleBbox.yMax,
        };
    }
}
exports.AltiumSchematicTransformer = AltiumSchematicTransformer;
//# sourceMappingURL=AltiumSchematicTransformer.js.map